<template>
    <div class="page missions-records-page">
        <div class="page-header">
            <h1>紀錄追蹤</h1>
            <p>查看任務進度紀錄與統計資訊</p>
        </div>

        <div class="content vaporwave-theme">
            <!-- 工具欄 -->
            <div class="toolbar">
                <a-collapse
                    v-model:activeKey="filterCollapseActiveKey"
                    :bordered="false"
                    class="filter-collapse"
                >
                    <a-collapse-panel key="filters" :showArrow="false">
                        <template #header>
                            <div class="filter-header">
                                <span class="filter-header-title">
                                    <FilterOutlined />
                                    篩選條件
                                </span>
                                <a-badge
                                    :count="getActiveFilterCount()"
                                    :number-style="{
                                        backgroundColor: '#2a4a7a'
                                    }"
                                />
                            </div>
                        </template>

                        <div class="filters-content">
                            <!-- 基本篩選 -->
                            <div class="filter-group">
                                <div class="filter-group-title">基本篩選</div>
                                <div class="filter-row">
                                    <a-select
                                        v-model:value="filters.user_id"
                                        show-search
                                        :filter-option="false"
                                        :options="userOptions"
                                        :loading="userLoading"
                                        placeholder="搜尋會員"
                                        allow-clear
                                        size="middle"
                                        class="vaporwave-select filter-item"
                                        @search="onUserSearch"
                                        @change="handleFilterChange"
                                        @clear="handleFilterChange"
                                    >
                                        <template #option="{ label, value }">
                                            <div class="user-option">
                                                <span class="user-name">
                                                    {{ label }}
                                                </span>
                                                <span class="user-id">
                                                    #{{ value }}
                                                </span>
                                            </div>
                                        </template>
                                    </a-select>
                                    <a-select
                                        v-model:value="filters.status"
                                        placeholder="狀態"
                                        allow-clear
                                        size="middle"
                                        class="vaporwave-select filter-item"
                                        :options="statusOptions"
                                        @change="handleFilterChange"
                                    />
                                    <a-select
                                        v-model:value="filters.identity"
                                        placeholder="身份"
                                        allow-clear
                                        size="middle"
                                        class="vaporwave-select filter-item"
                                        :options="identityOptions"
                                        @change="handleFilterChange"
                                    />
                                    <a-select
                                        v-model:value="filters.rank_id"
                                        placeholder="聘階"
                                        allow-clear
                                        size="middle"
                                        class="vaporwave-select filter-item"
                                        :options="rankOptions"
                                        :loading="rankLoading"
                                        @change="handleFilterChange"
                                    />
                                    <a-select
                                        v-model:value="filters.department_id"
                                        placeholder="事業部"
                                        allow-clear
                                        size="middle"
                                        class="vaporwave-select filter-item"
                                        :options="departmentOptions"
                                        :loading="departmentLoading"
                                        @change="handleFilterChange"
                                    />
                                </div>
                            </div>

                            <!-- 任務樹篩選 -->
                            <div class="filter-group">
                                <div class="filter-group-title">任務樹篩選</div>
                                <div class="filter-row">
                                    <a-select
                                        v-model:value="nodePath.missionId"
                                        placeholder="主線"
                                        allow-clear
                                        size="middle"
                                        class="vaporwave-select filter-item"
                                        :options="missionSelectOptions"
                                        :loading="loadingMissions"
                                        show-search
                                        option-filter-prop="label"
                                        @change="handleMissionChange"
                                        @clear="handleNodePathClear"
                                    />
                                    <a-select
                                        v-model:value="nodePath.chapterId"
                                        placeholder="章節"
                                        allow-clear
                                        size="middle"
                                        class="vaporwave-select filter-item"
                                        :options="chapterSelectOptions"
                                        :loading="loadingChapters"
                                        :disabled="!nodePath.missionId"
                                        show-search
                                        option-filter-prop="label"
                                        @change="handleChapterChange"
                                        @clear="handleNodePathClear"
                                    />
                                    <a-select
                                        v-model:value="nodePath.taskId"
                                        placeholder="任務"
                                        allow-clear
                                        size="middle"
                                        class="vaporwave-select filter-item"
                                        :options="taskSelectOptions"
                                        :loading="loadingTasks"
                                        :disabled="!nodePath.chapterId"
                                        show-search
                                        option-filter-prop="label"
                                        @change="handleTaskChange"
                                        @clear="handleNodePathClear"
                                    />
                                    <a-select
                                        v-model:value="nodePath.stepId"
                                        placeholder="步驟"
                                        allow-clear
                                        size="middle"
                                        class="vaporwave-select filter-item"
                                        :options="stepSelectOptions"
                                        :loading="loadingSteps"
                                        :disabled="!nodePath.taskId"
                                        show-search
                                        option-filter-prop="label"
                                        @change="handleStepChange"
                                        @clear="handleNodePathClear"
                                    />
                                </div>
                            </div>

                            <!-- 日期篩選 -->
                            <div class="filter-group">
                                <div class="filter-group-title">日期範圍</div>
                                <div class="filter-row">
                                    <a-range-picker
                                        v-model:value="dateRange"
                                        size="middle"
                                        class="filter-item"
                                        format="YYYY-MM-DD"
                                        :placeholder="['開始日期', '結束日期']"
                                        @change="handleDateRangeChange"
                                    />
                                </div>
                            </div>
                        </div>
                    </a-collapse-panel>
                </a-collapse>

                <!-- 操作按鈕區 -->
                <div class="toolbar-actions">
                    <a-space>
                        <div class="view-mode-legend">
                            <InfoCircleOutlined class="legend-icon" />
                            <span class="legend-text">
                                <UserOutlined class="legend-icon-small" />
                                總執行者
                                <span class="legend-divider">|</span>
                                <CheckCircleOutlined
                                    class="legend-icon-small legend-icon-success"
                                />
                                完成數量
                                <span class="legend-divider">|</span>
                                <span class="legend-dot completed"></span>
                                已完成
                                <span class="legend-divider">|</span>
                                <span class="legend-dot processing"></span>
                                進行中
                                <span class="legend-divider">|</span>
                                <span class="legend-dot error"></span>
                                失敗
                            </span>
                        </div>
                        <a-radio-group
                            v-model:value="viewMode"
                            button-style="solid"
                            size="middle"
                            class="view-mode-switch"
                        >
                            <a-radio-button value="list">
                                <UnorderedListOutlined />
                                記錄列表
                            </a-radio-button>
                            <a-radio-button value="tabs">
                                <AppstoreOutlined />
                                標籤頁視圖
                            </a-radio-button>
                        </a-radio-group>
                        <a-button
                            size="middle"
                            class="unified-reset-btn"
                            @click="handleReset"
                        >
                            重置
                        </a-button>
                    </a-space>
                </div>
            </div>

            <!-- 記錄列表視圖 -->
            <div v-if="viewMode === 'list'" class="records-list-view">
                <DataTable
                    :columns="progressColumns"
                    :data-source="progressList"
                    :pagination="progressPagination"
                    :loading="loading"
                    @refresh="loadProgressList"
                    @change="handleProgressTableChange"
                    class="progress-table"
                >
                    <template #bodyCell="{ column, record }">
                        <template v-if="column.key === 'user_name'">
                            <span
                                v-if="record.user?.name"
                                class="clickable-filter"
                                @click="(e) => handleUserClick(e, record.user)"
                            >
                                {{ record.user.name }}
                            </span>
                            <span v-else>-</span>
                        </template>
                        <template v-else-if="column.key === 'user_email'">
                            <span class="email-text">
                                {{ record.user?.email || '-' }}
                            </span>
                        </template>
                        <template v-else-if="column.key === 'user_rank'">
                            <a-tag
                                v-if="record.user?.rank"
                                size="small"
                                class="rank-tag clickable-filter"
                                @click="
                                    (e) => handleRankClick(e, record.user.rank)
                                "
                            >
                                {{ record.user.rank.name }}
                            </a-tag>
                            <a-tag
                                v-else-if="record.user?.identity_display"
                                size="small"
                                class="identity-tag"
                            >
                                {{ record.user.identity_display }}
                            </a-tag>
                            <span v-else>-</span>
                        </template>
                        <template v-else-if="column.key === 'user_department'">
                            <a-tag
                                v-if="record.user?.department"
                                size="small"
                                class="department-tag clickable-filter"
                                @click="
                                    (e) =>
                                        handleDepartmentClick(
                                            e,
                                            record.user.department
                                        )
                                "
                            >
                                {{ record.user.department.name }}
                            </a-tag>
                            <span v-else>-</span>
                        </template>
                        <template v-else-if="column.key === 'node_hierarchy'">
                            <a-popover
                                v-if="record.node?.hierarchy"
                                placement="topLeft"
                                :trigger="[]"
                                :open="
                                    activeHierarchyPopover === record.node.id
                                "
                                :overlay-style="{
                                    transition: 'none !important',
                                    animation: 'none !important'
                                }"
                                :mouseEnterDelay="0.1"
                                :mouseLeaveDelay="0.3"
                            >
                                <template #content>
                                    <div
                                        class="hierarchy-popover"
                                        @mouseenter="
                                            () =>
                                                openHierarchyPopover(
                                                    record.node.id
                                                )
                                        "
                                        @mouseleave="
                                            () =>
                                                closeHierarchyPopover(
                                                    record.node.id
                                                )
                                        "
                                    >
                                        <div
                                            v-if="record.node.hierarchy.mission"
                                            class="hierarchy-popover-item"
                                        >
                                            <span
                                                class="hierarchy-popover-label"
                                            >
                                                主線：
                                            </span>
                                            <span
                                                class="hierarchy-popover-value"
                                            >
                                                {{
                                                    record.node.hierarchy
                                                        .mission.name
                                                }}
                                            </span>
                                        </div>
                                        <div
                                            v-if="record.node.hierarchy.chapter"
                                            class="hierarchy-popover-item"
                                        >
                                            <span
                                                class="hierarchy-popover-label"
                                            >
                                                章節：
                                            </span>
                                            <span
                                                class="hierarchy-popover-value"
                                            >
                                                {{
                                                    record.node.hierarchy
                                                        .chapter.name
                                                }}
                                            </span>
                                        </div>
                                        <div
                                            v-if="record.node.hierarchy.task"
                                            class="hierarchy-popover-item"
                                        >
                                            <span
                                                class="hierarchy-popover-label"
                                            >
                                                任務：
                                            </span>
                                            <span
                                                class="hierarchy-popover-value"
                                            >
                                                {{
                                                    record.node.hierarchy.task
                                                        .name
                                                }}
                                            </span>
                                        </div>
                                        <div
                                            v-if="record.node.hierarchy.step"
                                            class="hierarchy-popover-item"
                                        >
                                            <span
                                                class="hierarchy-popover-label"
                                            >
                                                步驟：
                                            </span>
                                            <span
                                                class="hierarchy-popover-value"
                                            >
                                                {{
                                                    record.node.hierarchy.step
                                                        .name
                                                }}
                                            </span>
                                        </div>
                                    </div>
                                </template>
                                <div
                                    class="node-hierarchy-cell"
                                    @mouseenter="
                                        () =>
                                            openHierarchyPopover(record.node.id)
                                    "
                                    @mouseleave="
                                        () =>
                                            closeHierarchyPopover(
                                                record.node.id
                                            )
                                    "
                                >
                                    <div class="node-hierarchy-content">
                                        <div class="node-info">
                                            <a-tag
                                                v-if="record.node?.node_type"
                                                size="small"
                                                :class="[
                                                    'node-type-tag',
                                                    `node-type-${record.node.node_type}`
                                                ]"
                                            >
                                                {{
                                                    getNodeTypeLabel(
                                                        record.node.node_type
                                                    )
                                                }}
                                            </a-tag>
                                            <span
                                                class="node-name clickable-filter"
                                                @click="
                                                    (e) =>
                                                        handleNodeClick(
                                                            e,
                                                            record.node
                                                        )
                                                "
                                            >
                                                {{ record.node?.name || '-' }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </a-popover>
                            <div v-else class="node-hierarchy-cell">
                                <div class="node-hierarchy-content">
                                    <div class="node-info">
                                        <a-tag
                                            v-if="record.node?.node_type"
                                            size="small"
                                            :class="[
                                                'node-type-tag',
                                                `node-type-${record.node.node_type}`
                                            ]"
                                        >
                                            {{
                                                getNodeTypeLabel(
                                                    record.node.node_type
                                                )
                                            }}
                                        </a-tag>
                                        <span class="node-name">
                                            {{ record.node?.name || '-' }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </template>
                        <template v-else-if="column.key === 'status'">
                            <a-tag :color="getStatusColor(record.status)">
                                {{ getStatusLabel(record.status) }}
                            </a-tag>
                        </template>
                        <template
                            v-else-if="column.key === 'conditions_summary'"
                        >
                            <div
                                v-if="record.conditions_summary"
                                class="conditions-summary"
                            >
                                <span class="summary-text">
                                    {{ record.conditions_summary.completed }}
                                    /
                                    {{ record.conditions_summary.total }}
                                </span>
                                <span
                                    v-if="
                                        record.conditions_summary
                                            .pending_review > 0
                                    "
                                    class="pending-review clickable"
                                    @click="handleGoToReviews(record)"
                                >
                                    待審核：{{
                                        record.conditions_summary.pending_review
                                    }}
                                </span>
                            </div>
                            <span v-else>-</span>
                        </template>
                        <template v-else-if="column.key === 'started_at'">
                            {{ formatDateTime(record.started_at) || '-' }}
                        </template>
                        <template v-else-if="column.key === 'completed_at'">
                            {{ formatDateTime(record.completed_at) || '-' }}
                        </template>
                        <template v-else-if="column.key === 'action'">
                            <a-space class="action-buttons">
                                <a-button
                                    v-if="canViewRecord"
                                    size="small"
                                    class="action-btn-view"
                                    @click="handleViewProgressDetail(record)"
                                >
                                    查看
                                </a-button>
                                <a-button
                                    v-if="canEditRecord"
                                    size="small"
                                    class="action-btn-edit"
                                    @click="handleEditProgress(record)"
                                >
                                    編輯
                                </a-button>
                                <a-button
                                    v-if="
                                        canDeleteRecord &&
                                        record.status !== 'completed'
                                    "
                                    size="small"
                                    danger
                                    class="action-btn-delete"
                                    @click="
                                        handleDeleteProgress({
                                            user: record.user,
                                            id: record.id,
                                            status: record.status,
                                            node: record.node,
                                            started_at: record.started_at,
                                            completed_at: record.completed_at
                                        })
                                    "
                                >
                                    刪除
                                </a-button>
                            </a-space>
                        </template>
                    </template>
                </DataTable>
            </div>

            <!-- 標籤頁視圖 -->
            <div v-if="viewMode === 'tabs'" class="tabs-stats-view">
                <div v-if="loading" class="loading-container">
                    <a-spin size="large" />
                </div>
                <div
                    v-else-if="treeStatsData.length === 0"
                    class="empty-container"
                >
                    <a-empty description="暫無資料" />
                </div>
                <div v-else class="tabs-stats-content">
                    <a-tabs
                        v-model:activeKey="activeTabKey"
                        type="card"
                        class="mission-tabs"
                    >
                        <a-tab-pane
                            v-for="mission in treeStatsData"
                            :key="`mission-${mission.node.id}`"
                            :tab="mission.node.name"
                        >
                            <div class="tab-mission-content">
                                <!-- 主線資訊 -->
                                <div class="tab-mission-header-text">
                                    <div class="node-title-row">
                                        <a-tag
                                            :color="
                                                getNodeTypeColor(
                                                    mission.node.node_type
                                                )
                                            "
                                            size="small"
                                        >
                                            {{
                                                getNodeTypeLabel(
                                                    mission.node.node_type
                                                )
                                            }}
                                        </a-tag>
                                        <span class="node-name-text">
                                            {{ mission.node.name }}
                                        </span>
                                    </div>
                                    <div class="node-stats-text">
                                        <span class="stat-text-item">
                                            <UserOutlined class="stat-icon" />
                                            {{
                                                mission.stats?.total_users || 0
                                            }}人
                                        </span>
                                        <span class="stat-text-item">
                                            完成率：{{
                                                (
                                                    mission.stats
                                                        ?.completion_rate || 0
                                                ).toFixed(1)
                                            }}%
                                        </span>
                                        <span class="stat-text-item">
                                            已完成：{{
                                                mission.stats?.completed || 0
                                            }}
                                        </span>
                                        <span class="stat-text-item">
                                            進行中：{{
                                                mission.stats?.in_progress || 0
                                            }}
                                        </span>
                                        <span
                                            v-if="
                                                (mission.stats?.failed || 0) > 0
                                            "
                                            class="stat-text-item error"
                                        >
                                            失敗：{{
                                                mission.stats?.failed || 0
                                            }}
                                        </span>
                                        <span
                                            v-if="
                                                (mission.stats?.cancelled ||
                                                    0) > 0
                                            "
                                            class="stat-text-item cancelled"
                                        >
                                            已取消：{{
                                                mission.stats?.cancelled || 0
                                            }}
                                        </span>
                                        <a-button
                                            type="link"
                                            size="small"
                                            class="view-users-btn-text"
                                            @click="
                                                handleViewNodeUsers(
                                                    mission.node.id
                                                )
                                            "
                                        >
                                            <UserOutlined />
                                            查看參與者
                                        </a-button>
                                    </div>
                                </div>

                                <!-- 章節列表 -->
                                <div
                                    v-if="
                                        mission.chapters &&
                                        mission.chapters.length > 0
                                    "
                                    class="tab-chapters-list"
                                >
                                    <a-collapse
                                        :activeKey="
                                            getChapterKeys(mission.node.id)
                                        "
                                        :bordered="false"
                                        class="tab-chapter-accordion"
                                        @change="
                                            (keys) =>
                                                setChapterKeys(
                                                    mission.node.id,
                                                    keys
                                                )
                                        "
                                    >
                                        <a-collapse-panel
                                            v-for="chapter in mission.chapters"
                                            :key="`chapter-${chapter.node.id}`"
                                            class="tab-chapter-panel"
                                        >
                                            <template #header>
                                                <div
                                                    class="tab-accordion-header-text"
                                                >
                                                    <div class="node-title-row">
                                                        <a-tag
                                                            :color="
                                                                getNodeTypeColor(
                                                                    chapter.node
                                                                        .node_type
                                                                )
                                                            "
                                                            size="small"
                                                        >
                                                            {{
                                                                getNodeTypeLabel(
                                                                    chapter.node
                                                                        .node_type
                                                                )
                                                            }}
                                                        </a-tag>
                                                        <span
                                                            class="node-name-text"
                                                        >
                                                            {{
                                                                chapter.node
                                                                    .name
                                                            }}
                                                        </span>
                                                    </div>
                                                    <div
                                                        class="node-stats-text"
                                                    >
                                                        <span
                                                            class="stat-text-item"
                                                        >
                                                            <UserOutlined
                                                                class="stat-icon"
                                                            />
                                                            {{
                                                                chapter.stats
                                                                    ?.total_users ||
                                                                0
                                                            }}人
                                                        </span>
                                                        <span
                                                            class="stat-text-item"
                                                        >
                                                            完成率：{{
                                                                (
                                                                    chapter
                                                                        .stats
                                                                        ?.completion_rate ||
                                                                    0
                                                                ).toFixed(1)
                                                            }}%
                                                        </span>
                                                        <span
                                                            class="stat-text-item"
                                                        >
                                                            已完成：{{
                                                                chapter.stats
                                                                    ?.completed ||
                                                                0
                                                            }}
                                                        </span>
                                                        <span
                                                            class="stat-text-item"
                                                        >
                                                            進行中：{{
                                                                chapter.stats
                                                                    ?.in_progress ||
                                                                0
                                                            }}
                                                        </span>
                                                        <span
                                                            v-if="
                                                                (chapter.stats
                                                                    ?.failed ||
                                                                    0) > 0
                                                            "
                                                            class="stat-text-item error"
                                                        >
                                                            失敗：{{
                                                                chapter.stats
                                                                    ?.failed ||
                                                                0
                                                            }}
                                                        </span>
                                                        <span
                                                            v-if="
                                                                (chapter.stats
                                                                    ?.cancelled ||
                                                                    0) > 0
                                                            "
                                                            class="stat-text-item cancelled"
                                                        >
                                                            已取消：{{
                                                                chapter.stats
                                                                    ?.cancelled ||
                                                                0
                                                            }}
                                                        </span>
                                                        <a-button
                                                            type="link"
                                                            size="small"
                                                            class="view-users-btn-text"
                                                            @click.stop="
                                                                handleViewNodeUsers(
                                                                    chapter.node
                                                                        .id
                                                                )
                                                            "
                                                        >
                                                            <UserOutlined />
                                                            查看參與者
                                                        </a-button>
                                                    </div>
                                                </div>
                                            </template>
                                            <div
                                                v-if="
                                                    chapter.tasks &&
                                                    chapter.tasks.length > 0
                                                "
                                                class="tab-tasks-list"
                                            >
                                                <a-collapse
                                                    :activeKey="
                                                        getTaskKeys(
                                                            chapter.node.id
                                                        )
                                                    "
                                                    :bordered="false"
                                                    class="tab-task-accordion"
                                                    @change="
                                                        (keys) =>
                                                            setTaskKeys(
                                                                chapter.node.id,
                                                                keys
                                                            )
                                                    "
                                                >
                                                    <a-collapse-panel
                                                        v-for="task in chapter.tasks"
                                                        :key="`task-${task.node.id}`"
                                                        class="tab-task-panel"
                                                    >
                                                        <template #header>
                                                            <div
                                                                class="tab-accordion-header-text"
                                                            >
                                                                <div
                                                                    class="node-title-row"
                                                                >
                                                                    <a-tag
                                                                        :color="
                                                                            getNodeTypeColor(
                                                                                task
                                                                                    .node
                                                                                    .node_type
                                                                            )
                                                                        "
                                                                        size="small"
                                                                    >
                                                                        {{
                                                                            getNodeTypeLabel(
                                                                                task
                                                                                    .node
                                                                                    .node_type
                                                                            )
                                                                        }}
                                                                    </a-tag>
                                                                    <span
                                                                        class="node-name-text"
                                                                    >
                                                                        {{
                                                                            task
                                                                                .node
                                                                                .name
                                                                        }}
                                                                    </span>
                                                                </div>
                                                                <div
                                                                    class="node-stats-text"
                                                                >
                                                                    <span
                                                                        class="stat-text-item"
                                                                    >
                                                                        <UserOutlined
                                                                            class="stat-icon"
                                                                        />
                                                                        {{
                                                                            task
                                                                                .stats
                                                                                ?.total_users ||
                                                                            0
                                                                        }}人
                                                                    </span>
                                                                    <span
                                                                        class="stat-text-item"
                                                                    >
                                                                        完成率：{{
                                                                            (
                                                                                task
                                                                                    .stats
                                                                                    ?.completion_rate ||
                                                                                0
                                                                            ).toFixed(
                                                                                1
                                                                            )
                                                                        }}%
                                                                    </span>
                                                                    <span
                                                                        class="stat-text-item"
                                                                    >
                                                                        已完成：{{
                                                                            task
                                                                                .stats
                                                                                ?.completed ||
                                                                            0
                                                                        }}
                                                                    </span>
                                                                    <span
                                                                        class="stat-text-item"
                                                                    >
                                                                        進行中：{{
                                                                            task
                                                                                .stats
                                                                                ?.in_progress ||
                                                                            0
                                                                        }}
                                                                    </span>
                                                                    <span
                                                                        v-if="
                                                                            (task
                                                                                .stats
                                                                                ?.failed ||
                                                                                0) >
                                                                            0
                                                                        "
                                                                        class="stat-text-item error"
                                                                    >
                                                                        失敗：{{
                                                                            task
                                                                                .stats
                                                                                ?.failed ||
                                                                            0
                                                                        }}
                                                                    </span>
                                                                    <span
                                                                        v-if="
                                                                            (task
                                                                                .stats
                                                                                ?.cancelled ||
                                                                                0) >
                                                                            0
                                                                        "
                                                                        class="stat-text-item cancelled"
                                                                    >
                                                                        已取消：{{
                                                                            task
                                                                                .stats
                                                                                ?.cancelled ||
                                                                            0
                                                                        }}
                                                                    </span>
                                                                    <a-button
                                                                        type="link"
                                                                        size="small"
                                                                        class="view-users-btn-text"
                                                                        @click.stop="
                                                                            handleViewNodeUsers(
                                                                                task
                                                                                    .node
                                                                                    .id
                                                                            )
                                                                        "
                                                                    >
                                                                        <UserOutlined />
                                                                        查看參與者
                                                                    </a-button>
                                                                </div>
                                                            </div>
                                                        </template>
                                                        <div
                                                            v-if="
                                                                task.steps &&
                                                                task.steps
                                                                    .length > 0
                                                            "
                                                            class="tab-steps-list"
                                                        >
                                                            <div
                                                                v-for="step in task.steps"
                                                                :key="
                                                                    step.node.id
                                                                "
                                                                class="tab-step-item-text"
                                                            >
                                                                <div
                                                                    class="node-title-row"
                                                                >
                                                                    <a-tag
                                                                        :color="
                                                                            getNodeTypeColor(
                                                                                step
                                                                                    .node
                                                                                    .node_type
                                                                            )
                                                                        "
                                                                        size="small"
                                                                    >
                                                                        {{
                                                                            getNodeTypeLabel(
                                                                                step
                                                                                    .node
                                                                                    .node_type
                                                                            )
                                                                        }}
                                                                    </a-tag>
                                                                    <span
                                                                        class="node-name-text"
                                                                    >
                                                                        {{
                                                                            step
                                                                                .node
                                                                                .name
                                                                        }}
                                                                    </span>
                                                                </div>
                                                                <div
                                                                    class="node-stats-text"
                                                                >
                                                                    <span
                                                                        class="stat-text-item"
                                                                    >
                                                                        <UserOutlined
                                                                            class="stat-icon"
                                                                        />
                                                                        {{
                                                                            step
                                                                                .stats
                                                                                ?.total_users ||
                                                                            0
                                                                        }}人
                                                                    </span>
                                                                    <span
                                                                        class="stat-text-item"
                                                                    >
                                                                        完成率：{{
                                                                            (
                                                                                step
                                                                                    .stats
                                                                                    ?.completion_rate ||
                                                                                0
                                                                            ).toFixed(
                                                                                1
                                                                            )
                                                                        }}%
                                                                    </span>
                                                                    <span
                                                                        class="stat-text-item"
                                                                    >
                                                                        已完成：{{
                                                                            step
                                                                                .stats
                                                                                ?.completed ||
                                                                            0
                                                                        }}
                                                                    </span>
                                                                    <span
                                                                        class="stat-text-item"
                                                                    >
                                                                        進行中：{{
                                                                            step
                                                                                .stats
                                                                                ?.in_progress ||
                                                                            0
                                                                        }}
                                                                    </span>
                                                                    <span
                                                                        v-if="
                                                                            (step
                                                                                .stats
                                                                                ?.failed ||
                                                                                0) >
                                                                            0
                                                                        "
                                                                        class="stat-text-item error"
                                                                    >
                                                                        失敗：{{
                                                                            step
                                                                                .stats
                                                                                ?.failed ||
                                                                            0
                                                                        }}
                                                                    </span>
                                                                    <span
                                                                        v-if="
                                                                            (step
                                                                                .stats
                                                                                ?.cancelled ||
                                                                                0) >
                                                                            0
                                                                        "
                                                                        class="stat-text-item cancelled"
                                                                    >
                                                                        已取消：{{
                                                                            step
                                                                                .stats
                                                                                ?.cancelled ||
                                                                            0
                                                                        }}
                                                                    </span>
                                                                    <a-button
                                                                        type="link"
                                                                        size="small"
                                                                        class="view-users-btn-text"
                                                                        @click="
                                                                            handleViewNodeUsers(
                                                                                step
                                                                                    .node
                                                                                    .id
                                                                            )
                                                                        "
                                                                    >
                                                                        <UserOutlined />
                                                                        查看參與者
                                                                    </a-button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </a-collapse-panel>
                                                </a-collapse>
                                            </div>
                                        </a-collapse-panel>
                                    </a-collapse>
                                </div>
                            </div>
                        </a-tab-pane>
                    </a-tabs>
                </div>
            </div>
        </div>

        <!-- 節點執行者列表 Modal -->
        <a-modal
            v-model:open="nodeUsersModalVisible"
            :title="nodeUsersModalTitle"
            :width="1000"
            :footer="null"
            :z-index="1500"
            class="node-users-modal vaporwave-modal"
            @cancel="handleNodeUsersModalCancel"
        >
            <div class="node-users-content">
                <!-- 節點資訊摘要 -->
                <div v-if="currentNodeUsersData" class="node-info-summary">
                    <div class="summary-header">
                        <h3 class="summary-title">
                            {{ currentNodeUsersData.node?.name || '-' }}
                        </h3>
                        <a-tag
                            v-if="currentNodeUsersData.node?.node_type"
                            :color="
                                getNodeTypeColor(
                                    currentNodeUsersData.node.node_type
                                )
                            "
                            class="summary-type-tag"
                        >
                            {{
                                getNodeTypeLabel(
                                    currentNodeUsersData.node.node_type
                                )
                            }}
                        </a-tag>
                    </div>
                    <div
                        v-if="currentNodeUsersData.stats"
                        class="summary-stats"
                    >
                        <div class="summary-stat-card">
                            <div class="summary-stat-label">總參與者</div>
                            <div class="summary-stat-value">
                                {{
                                    currentNodeUsersData.stats.total_users || 0
                                }}
                            </div>
                        </div>
                        <div class="summary-stat-card">
                            <div class="summary-stat-label">總記錄數</div>
                            <div class="summary-stat-value">
                                {{
                                    currentNodeUsersData.stats.total_progress ||
                                    0
                                }}
                            </div>
                        </div>
                        <div class="summary-stat-card">
                            <div class="summary-stat-label">已完成</div>
                            <div class="summary-stat-value success">
                                {{ currentNodeUsersData.stats.completed || 0 }}
                            </div>
                        </div>
                        <div class="summary-stat-card highlight">
                            <div class="summary-stat-label">完成率</div>
                            <div class="summary-stat-value highlight">
                                {{
                                    (
                                        currentNodeUsersData.stats
                                            .completion_rate || 0
                                    ).toFixed(1)
                                }}%
                            </div>
                        </div>
                    </div>
                </div>
                <div v-else class="loading-container">
                    <a-spin />
                </div>

                <!-- 參與者列表 -->
                <div class="users-list-section">
                    <!-- 搜尋和篩選 -->
                    <div class="node-users-filters">
                        <a-input
                            v-model:value="nodeUsersSearchText"
                            placeholder="搜尋參與者姓名或 Email"
                            allow-clear
                            class="search-input"
                            @press-enter="handleNodeUsersSearch"
                            @clear="handleNodeUsersSearch"
                        >
                            <template #prefix>
                                <SearchOutlined />
                            </template>
                        </a-input>
                        <a-select
                            v-model:value="nodeUsersFilters.status"
                            placeholder="狀態"
                            allow-clear
                            class="filter-select"
                            :options="statusOptions"
                            @change="handleNodeUsersFilterChange"
                        />
                        <a-select
                            v-model:value="nodeUsersFilters.identity"
                            placeholder="身份"
                            allow-clear
                            class="filter-select"
                            :options="identityOptions"
                            @change="handleNodeUsersFilterChange"
                        />
                        <a-select
                            v-model:value="nodeUsersFilters.rank_id"
                            placeholder="聘階"
                            allow-clear
                            class="filter-select"
                            :options="rankOptions"
                            :loading="rankLoading"
                            @change="handleNodeUsersFilterChange"
                        />
                        <a-select
                            v-model:value="nodeUsersFilters.department_id"
                            placeholder="事業部"
                            allow-clear
                            class="filter-select"
                            :options="departmentOptions"
                            :loading="departmentLoading"
                            @change="handleNodeUsersFilterChange"
                        />
                        <a-button
                            type="primary"
                            class="vaporwave-btn search-btn"
                            @click="handleNodeUsersSearch"
                        >
                            搜尋
                        </a-button>
                    </div>
                    <DataTable
                        :columns="nodeUsersColumns"
                        :data-source="nodeUsersList"
                        :pagination="nodeUsersPagination"
                        :loading="nodeUsersLoading"
                        @refresh="loadNodeUsers"
                        @change="handleNodeUsersTableChange"
                        class="node-users-table"
                    >
                        <template #bodyCell="{ column, record }">
                            <template v-if="column.key === 'user_name'">
                                {{ record.user?.name || '-' }}
                            </template>
                            <template v-else-if="column.key === 'user_email'">
                                {{ record.user?.email || '-' }}
                            </template>
                            <template v-else-if="column.key === 'user_rank'">
                                <a-tag
                                    v-if="record.user?.rank"
                                    size="small"
                                    class="rank-tag"
                                >
                                    {{ record.user.rank.name }}
                                </a-tag>
                                <span v-else>-</span>
                            </template>
                            <template
                                v-else-if="column.key === 'user_department'"
                            >
                                <a-tag
                                    v-if="record.user?.department"
                                    size="small"
                                    class="department-tag"
                                >
                                    {{ record.user.department.name }}
                                </a-tag>
                                <span v-else>-</span>
                            </template>
                            <template
                                v-else-if="column.key === 'progress_status'"
                            >
                                <a-tag
                                    :color="
                                        getStatusColor(record.progress?.status)
                                    "
                                >
                                    {{
                                        getStatusLabel(record.progress?.status)
                                    }}
                                </a-tag>
                            </template>
                            <template
                                v-else-if="column.key === 'progress_started_at'"
                            >
                                {{
                                    record.progress?.started_at
                                        ? formatDateTime(
                                              record.progress.started_at
                                          )
                                        : '-'
                                }}
                            </template>
                            <template
                                v-else-if="
                                    column.key === 'progress_completed_at'
                                "
                            >
                                {{
                                    record.progress?.completed_at
                                        ? formatDateTime(
                                              record.progress.completed_at
                                          )
                                        : '-'
                                }}
                            </template>
                            <template
                                v-else-if="column.key === 'attempt_count'"
                            >
                                {{ record.attempt_count || 0 }} 次
                            </template>
                            <template v-else-if="column.key === 'action'">
                                <a-space class="action-buttons">
                                    <a-button
                                        v-if="canViewRecord"
                                        size="small"
                                        class="action-btn-view"
                                        @click="
                                            handleViewProgressDetail({
                                                user: record.user,
                                                id: record.progress?.id,
                                                status: record.progress?.status,
                                                started_at:
                                                    record.progress?.started_at,
                                                completed_at:
                                                    record.progress
                                                        ?.completed_at,
                                                node_hierarchy:
                                                    record.node_hierarchy
                                            })
                                        "
                                    >
                                        查看
                                    </a-button>
                                    <a-button
                                        v-if="canEditRecord && record.progress"
                                        size="small"
                                        class="action-btn-edit"
                                        @click="
                                            handleEditProgress({
                                                user: record.user,
                                                id: record.progress.id,
                                                status: record.progress.status,
                                                started_at:
                                                    record.progress.started_at,
                                                completed_at:
                                                    record.progress
                                                        .completed_at,
                                                node_hierarchy:
                                                    record.node_hierarchy
                                            })
                                        "
                                    >
                                        編輯
                                    </a-button>
                                    <a-button
                                        v-if="
                                            canDeleteRecord &&
                                            record.progress &&
                                            record.progress.status !==
                                                'completed'
                                        "
                                        size="small"
                                        danger
                                        class="action-btn-delete"
                                        @click="
                                            handleDeleteProgress({
                                                user: record.user,
                                                id: record.progress.id,
                                                status: record.progress.status,
                                                node: record.node,
                                                started_at:
                                                    record.progress.started_at,
                                                completed_at:
                                                    record.progress.completed_at
                                            })
                                        "
                                    >
                                        刪除
                                    </a-button>
                                </a-space>
                            </template>
                        </template>
                    </DataTable>
                </div>
            </div>
        </a-modal>

        <!-- 進度記錄詳情 Modal -->
        <a-modal
            v-model:open="progressDetailModalVisible"
            title="進度記錄詳情"
            width="900px"
            :footer="null"
            :z-index="1501"
        >
            <a-spin :spinning="progressDetailLoading">
                <div v-if="progressDetailData" class="progress-detail-content">
                    <!-- 基本資訊 -->
                    <a-descriptions
                        :column="2"
                        bordered
                        style="margin-bottom: 24px"
                    >
                        <a-descriptions-item label="任務節點">
                            <div v-if="progressDetailData.node">
                                <div
                                    style="font-weight: 600; margin-bottom: 4px"
                                >
                                    {{ progressDetailData.node.name }}
                                </div>
                                <div
                                    v-if="progressDetailData.node.description"
                                    style="font-size: 12px; color: #666"
                                >
                                    {{ progressDetailData.node.description }}
                                </div>
                            </div>
                            <span v-else>-</span>
                        </a-descriptions-item>
                        <a-descriptions-item label="節點類型">
                            <a-tag>
                                {{
                                    progressDetailData.node?.node_type ===
                                    'mission'
                                        ? '主線'
                                        : progressDetailData.node?.node_type ===
                                            'chapter'
                                          ? '章節'
                                          : progressDetailData.node
                                                  ?.node_type === 'task'
                                            ? '任務'
                                            : progressDetailData.node
                                                    ?.node_type === 'step'
                                              ? '步驟'
                                              : progressDetailData.node
                                                    ?.node_type || '-'
                                }}
                            </a-tag>
                        </a-descriptions-item>
                        <a-descriptions-item label="進度狀態">
                            <a-tag
                                :color="
                                    progressDetailData.progress?.status ===
                                    'completed'
                                        ? 'green'
                                        : progressDetailData.progress
                                                ?.status === 'failed'
                                          ? 'red'
                                          : progressDetailData.progress
                                                  ?.status === 'in_progress'
                                            ? 'blue'
                                            : progressDetailData.progress
                                                    ?.status === 'cancelled'
                                              ? 'volcano'
                                              : progressDetailData.progress
                                                      ?.status === 'pending'
                                                ? 'orange'
                                                : 'default'
                                "
                            >
                                {{
                                    statusOptions.find(
                                        (opt) =>
                                            opt.value ===
                                            progressDetailData.progress?.status
                                    )?.label ||
                                    progressDetailData.progress?.status ||
                                    '-'
                                }}
                            </a-tag>
                        </a-descriptions-item>
                        <a-descriptions-item label="進度 ID">
                            {{ progressDetailData.progress?.id || '-' }}
                        </a-descriptions-item>
                        <a-descriptions-item label="開始時間">
                            {{
                                formatDateTime(
                                    progressDetailData.progress?.started_at
                                ) || '-'
                            }}
                        </a-descriptions-item>
                        <a-descriptions-item label="完成時間">
                            {{
                                formatDateTime(
                                    progressDetailData.progress?.completed_at
                                ) || '-'
                            }}
                        </a-descriptions-item>
                        <a-descriptions-item
                            v-if="progressDetailData.triggered_by"
                            label="觸發來源"
                            :span="2"
                        >
                            <div v-if="progressDetailData.triggered_by.user">
                                <div>
                                    使用者：{{
                                        progressDetailData.triggered_by.user
                                            .name || '-'
                                    }}
                                    ({{
                                        progressDetailData.triggered_by.user
                                            .email || '-'
                                    }})
                                </div>
                                <div
                                    v-if="
                                        progressDetailData.triggered_by.user
                                            .identity_name
                                    "
                                    style="font-size: 12px; color: #666"
                                >
                                    身份：{{
                                        progressDetailData.triggered_by.user
                                            .identity_name
                                    }}
                                </div>
                            </div>
                            <div
                                v-else-if="
                                    progressDetailData.triggered_by.progress
                                "
                            >
                                進度 ID：{{
                                    progressDetailData.triggered_by.progress
                                        .id || '-'
                                }}
                            </div>
                            <span v-else>-</span>
                        </a-descriptions-item>
                        <a-descriptions-item
                            v-if="progressDetailData.parent_progress"
                            label="父進度"
                            :span="2"
                        >
                            <div>
                                <div>
                                    進度 ID：{{
                                        progressDetailData.parent_progress.id ||
                                        '-'
                                    }}
                                </div>
                                <div>
                                    節點：{{
                                        progressDetailData.parent_progress.node
                                            ?.name || '-'
                                    }}
                                </div>
                                <div>
                                    狀態：
                                    <a-tag
                                        :color="
                                            progressDetailData.parent_progress
                                                .status === 'completed'
                                                ? 'green'
                                                : progressDetailData
                                                        .parent_progress
                                                        .status ===
                                                    'in_progress'
                                                  ? 'blue'
                                                  : 'default'
                                        "
                                    >
                                        {{
                                            statusOptions.find(
                                                (opt) =>
                                                    opt.value ===
                                                    progressDetailData
                                                        .parent_progress.status
                                            )?.label ||
                                            progressDetailData.parent_progress
                                                .status ||
                                            '-'
                                        }}
                                    </a-tag>
                                </div>
                            </div>
                        </a-descriptions-item>
                    </a-descriptions>

                    <!-- 時間軸 -->
                    <div
                        v-if="
                            progressDetailData.timeline &&
                            progressDetailData.timeline.length > 0
                        "
                        class="timeline-section"
                        style="margin-bottom: 24px"
                    >
                        <h3 style="margin-bottom: 12px">時間軸</h3>
                        <a-timeline>
                            <a-timeline-item
                                v-for="(
                                    event, index
                                ) in progressDetailData.timeline"
                                :key="index"
                                :color="
                                    event.event === 'completed'
                                        ? 'green'
                                        : event.event === 'failed'
                                          ? 'red'
                                          : 'blue'
                                "
                            >
                                <div style="font-weight: 600">
                                    {{ event.label }}
                                </div>
                                <div style="font-size: 12px; color: #666">
                                    {{ formatDateTime(event.timestamp) || '-' }}
                                </div>
                                <div
                                    v-if="event.description"
                                    style="
                                        font-size: 12px;
                                        color: #999;
                                        margin-top: 4px;
                                    "
                                >
                                    {{ event.description }}
                                </div>
                            </a-timeline-item>
                        </a-timeline>
                    </div>

                    <!-- 子進度 -->
                    <div
                        v-if="
                            progressDetailData.child_progresses &&
                            progressDetailData.child_progresses.length > 0
                        "
                        class="child-progresses-section"
                        style="margin-bottom: 24px"
                    >
                        <h3 style="margin-bottom: 12px">子進度</h3>
                        <a-table
                            :columns="[
                                { title: '進度 ID', key: 'id', width: 100 },
                                { title: '節點名稱', key: 'name', width: 200 },
                                {
                                    title: '節點類型',
                                    key: 'node_type',
                                    width: 100
                                },
                                { title: '狀態', key: 'status', width: 120 },
                                {
                                    title: '開始時間',
                                    key: 'started_at',
                                    width: 180
                                },
                                {
                                    title: '完成時間',
                                    key: 'completed_at',
                                    width: 180
                                }
                            ]"
                            :data-source="progressDetailData.child_progresses"
                            :pagination="false"
                            size="small"
                        >
                            <template #bodyCell="{ column, record }">
                                <template v-if="column.key === 'id'">
                                    {{ record.id || '-' }}
                                </template>
                                <template v-else-if="column.key === 'name'">
                                    {{ record.node?.name || '-' }}
                                </template>
                                <template
                                    v-else-if="column.key === 'node_type'"
                                >
                                    <a-tag>
                                        {{
                                            record.node?.node_type === 'mission'
                                                ? '主線'
                                                : record.node?.node_type ===
                                                    'chapter'
                                                  ? '章節'
                                                  : record.node?.node_type ===
                                                      'task'
                                                    ? '任務'
                                                    : record.node?.node_type ===
                                                        'step'
                                                      ? '步驟'
                                                      : record.node
                                                            ?.node_type || '-'
                                        }}
                                    </a-tag>
                                </template>
                                <template v-else-if="column.key === 'status'">
                                    <a-tag
                                        :color="
                                            record.status === 'completed'
                                                ? 'green'
                                                : record.status === 'failed'
                                                  ? 'red'
                                                  : record.status ===
                                                      'in_progress'
                                                    ? 'blue'
                                                    : record.status ===
                                                        'cancelled'
                                                      ? 'volcano'
                                                      : record.status ===
                                                          'pending'
                                                        ? 'orange'
                                                        : 'default'
                                        "
                                    >
                                        {{
                                            statusOptions.find(
                                                (opt) =>
                                                    opt.value === record.status
                                            )?.label ||
                                            record.status ||
                                            '-'
                                        }}
                                    </a-tag>
                                </template>
                                <template
                                    v-else-if="column.key === 'started_at'"
                                >
                                    {{
                                        formatDateTime(record.started_at) || '-'
                                    }}
                                </template>
                                <template
                                    v-else-if="column.key === 'completed_at'"
                                >
                                    {{
                                        formatDateTime(record.completed_at) ||
                                        '-'
                                    }}
                                </template>
                            </template>
                        </a-table>
                    </div>

                    <!-- 條件列表 -->
                    <div
                        v-if="
                            progressDetailData.conditions &&
                            progressDetailData.conditions.length > 0
                        "
                        class="conditions-section"
                        style="margin-bottom: 24px"
                    >
                        <h3 style="margin-bottom: 12px">條件列表</h3>
                        <a-collapse
                            v-model:activeKey="activeConditionKeys"
                            :bordered="false"
                        >
                            <a-collapse-panel
                                v-for="(
                                    condition, index
                                ) in progressDetailData.conditions"
                                :key="index"
                                :header="
                                    getConditionDisplayName(condition) ||
                                    `條件 ${index + 1}`
                                "
                            >
                                <div class="condition-detail">
                                    <a-descriptions
                                        :column="2"
                                        bordered
                                        size="small"
                                    >
                                        <a-descriptions-item label="條件類型">
                                            <a-tag>
                                                {{
                                                    condition.type_display ||
                                                    condition.type ||
                                                    '-'
                                                }}
                                            </a-tag>
                                        </a-descriptions-item>
                                        <a-descriptions-item label="條件狀態">
                                            <a-tag
                                                :color="
                                                    condition.result_status ===
                                                    'completed'
                                                        ? 'green'
                                                        : condition.result_status ===
                                                            'failed'
                                                          ? 'red'
                                                          : condition.result_status ===
                                                              'pending_review'
                                                            ? 'orange'
                                                            : condition.result_status ===
                                                                'rejected'
                                                              ? 'volcano'
                                                              : 'default'
                                                "
                                            >
                                                {{
                                                    condition.result_status_display ||
                                                    condition.result_status ||
                                                    '-'
                                                }}
                                            </a-tag>
                                        </a-descriptions-item>
                                        <a-descriptions-item
                                            label="條件名稱"
                                            :span="2"
                                        >
                                            {{
                                                getConditionDisplayName(
                                                    condition
                                                )
                                            }}
                                        </a-descriptions-item>
                                        <a-descriptions-item
                                            label="說明"
                                            :span="2"
                                        >
                                            {{ condition.description || '-' }}
                                        </a-descriptions-item>
                                        <a-descriptions-item label="需要審核">
                                            {{
                                                condition.require_review
                                                    ? '是'
                                                    : '否'
                                            }}
                                        </a-descriptions-item>
                                        <a-descriptions-item label="完成次數">
                                            {{ condition.complete_count || 0 }}
                                        </a-descriptions-item>
                                        <a-descriptions-item label="提交時間">
                                            {{
                                                condition.submitted_at
                                                    ? formatDateTime(
                                                          condition.submitted_at
                                                      )
                                                    : '-'
                                            }}
                                        </a-descriptions-item>
                                        <a-descriptions-item label="審核時間">
                                            {{
                                                condition.reviewed_at
                                                    ? formatDateTime(
                                                          condition.reviewed_at
                                                      )
                                                    : '-'
                                            }}
                                        </a-descriptions-item>
                                        <a-descriptions-item label="審核人員">
                                            {{ condition.reviewed_by || '-' }}
                                        </a-descriptions-item>
                                        <a-descriptions-item
                                            label="審核備註"
                                            :span="2"
                                        >
                                            {{ condition.review_note || '-' }}
                                        </a-descriptions-item>
                                    </a-descriptions>

                                    <!-- Metadata 詳細資訊 -->
                                    <div
                                        v-if="condition.metadata"
                                        class="metadata-section"
                                        style="margin-top: 16px"
                                    >
                                        <h4
                                            style="
                                                margin-bottom: 8px;
                                                font-size: 14px;
                                                font-weight: 600;
                                            "
                                        >
                                            詳細資訊
                                        </h4>
                                        <a-descriptions
                                            :column="2"
                                            bordered
                                            size="small"
                                        >
                                            <template
                                                v-for="(
                                                    value, key
                                                ) in condition.metadata"
                                                :key="key"
                                            >
                                                <!-- 跳過 manual_completed_by，只顯示 manual_completed_by_name -->
                                                <template
                                                    v-if="
                                                        key !==
                                                        'manual_completed_by'
                                                    "
                                                >
                                                    <a-descriptions-item
                                                        :label="
                                                            formatMetadataLabel(
                                                                key
                                                            )
                                                        "
                                                        :span="
                                                            getMetadataSpan(key)
                                                        "
                                                    >
                                                        <template
                                                            v-if="
                                                                key ===
                                                                'manual_completed'
                                                            "
                                                        >
                                                            {{
                                                                value
                                                                    ? '是'
                                                                    : '否'
                                                            }}
                                                        </template>
                                                        <template
                                                            v-else-if="
                                                                key.includes(
                                                                    '_at'
                                                                ) ||
                                                                key.includes(
                                                                    'time'
                                                                ) ||
                                                                key.includes(
                                                                    'date'
                                                                )
                                                            "
                                                        >
                                                            {{
                                                                formatDateTime(
                                                                    value
                                                                ) || value
                                                            }}
                                                        </template>
                                                        <template
                                                            v-else-if="
                                                                key ===
                                                                'manual_completed_by_name'
                                                            "
                                                        >
                                                            {{ value || '-' }}
                                                        </template>
                                                        <template
                                                            v-else-if="
                                                                key ===
                                                                'auto_verified'
                                                            "
                                                        >
                                                            {{
                                                                value
                                                                    ? '是'
                                                                    : '否'
                                                            }}
                                                        </template>
                                                        <template
                                                            v-else-if="
                                                                key === 'status'
                                                            "
                                                        >
                                                            {{
                                                                value ===
                                                                'completed'
                                                                    ? '已完成'
                                                                    : value ===
                                                                        'pending'
                                                                      ? '待處理'
                                                                      : value ===
                                                                          'in_progress'
                                                                        ? '進行中'
                                                                        : value ||
                                                                          '-'
                                                            }}
                                                        </template>
                                                        <template v-else>
                                                            {{ value }}
                                                        </template>
                                                    </a-descriptions-item>
                                                </template>
                                            </template>
                                        </a-descriptions>
                                    </div>
                                </div>
                            </a-collapse-panel>
                        </a-collapse>
                    </div>
                </div>
            </a-spin>
        </a-modal>

        <!-- 編輯進度記錄 Modal -->
        <a-modal
            v-model:open="editProgressModalVisible"
            title="編輯進度記錄"
            width="600px"
            :confirm-loading="editProgressLoading"
            :z-index="2000"
            wrap-class-name="edit-progress-modal-wrap"
            @ok="handleSubmitEditProgress"
            @cancel="editProgressModalVisible = false"
        >
            <!-- 快捷操作區 -->
            <div
                v-if="
                    currentEditRecord?.status === 'pending' ||
                    currentEditRecord?.status === 'in_progress' ||
                    currentEditRecord?.status === 'completed' ||
                    currentEditRecord?.status === 'failed' ||
                    currentEditRecord?.status === 'cancelled'
                "
                class="quick-actions-bar"
            >
                <a-button
                    v-if="
                        currentEditRecord?.status === 'pending' ||
                        currentEditRecord?.status === 'in_progress'
                    "
                    danger
                    :loading="editProgressLoading"
                    @click="handleCancelProgress"
                >
                    取消任務
                </a-button>
                <a-button
                    v-if="
                        currentEditRecord?.status === 'completed' ||
                        currentEditRecord?.status === 'failed' ||
                        currentEditRecord?.status === 'cancelled'
                    "
                    danger
                    :loading="editProgressLoading"
                    @click="handleResetProgress"
                >
                    重置任務
                </a-button>
            </div>
            <a-form
                ref="editProgressFormRef"
                :model="editProgressForm"
                :rules="editProgressFormRules"
                :label-col="{ span: 6 }"
                :wrapper-col="{ span: 18 }"
            >
                <a-form-item label="狀態" name="status">
                    <a-select
                        v-model:value="editProgressForm.status"
                        placeholder="請選擇狀態"
                        allow-clear
                        size="middle"
                        style="width: 100%"
                        :get-popup-container="
                            (trigger) => trigger.parentElement
                        "
                    >
                        <a-select-option value="pending">
                            待處理
                        </a-select-option>
                        <a-select-option value="in_progress">
                            進行中
                        </a-select-option>
                        <a-select-option value="completed">
                            已完成
                        </a-select-option>
                        <a-select-option value="failed">失敗</a-select-option>
                        <a-select-option value="cancelled">
                            已取消
                        </a-select-option>
                    </a-select>
                </a-form-item>
                <a-form-item label="開始時間" name="started_at">
                    <a-date-picker
                        v-model:value="editProgressForm.started_at"
                        format="YYYY-MM-DD HH:mm:ss"
                        placeholder="請選擇開始時間"
                        style="width: 100%"
                        :allow-clear="true"
                        @change="
                            (d) => {
                                editProgressForm.started_at =
                                    applyStartOfDay(d);
                            }
                        "
                    />
                </a-form-item>
                <a-form-item label="操作備註" name="note">
                    <a-textarea
                        v-model:value="editProgressForm.note"
                        :rows="4"
                        placeholder="請輸入操作備註（選填，最多 500 字元）"
                        :maxlength="500"
                        show-count
                    />
                </a-form-item>
            </a-form>
            <template #footer>
                <div class="edit-progress-footer">
                    <a-space>
                        <a-button @click="editProgressModalVisible = false">
                            取消
                        </a-button>
                        <a-button
                            type="primary"
                            :loading="editProgressLoading"
                            @click="handleSubmitEditProgress"
                        >
                            確定
                        </a-button>
                    </a-space>
                </div>
            </template>
        </a-modal>

        <!-- 刪除進度記錄 Modal -->
        <a-modal
            v-model:open="deleteModalVisible"
            title="刪除進度記錄"
            :width="600"
            :confirm-loading="deleteLoading"
            ok-text="確定"
            cancel-text="取消"
            @ok="handleConfirmDelete"
            @cancel="deleteModalVisible = false"
        >
            <div v-if="deleteProgressData" class="delete-modal-content">
                <!-- 進度記錄資訊 -->
                <div class="delete-progress-info">
                    <h3 class="delete-section-title">進度記錄資訊</h3>
                    <a-descriptions :column="2" bordered size="small">
                        <a-descriptions-item label="使用者">
                            <span class="progress-user-name">
                                {{ deleteProgressData.user?.name || '-' }}
                            </span>
                        </a-descriptions-item>
                        <a-descriptions-item label="Email">
                            <span class="progress-user-email">
                                {{ deleteProgressData.user?.email || '-' }}
                            </span>
                        </a-descriptions-item>
                        <a-descriptions-item label="節點名稱" :span="2">
                            <span class="progress-node-name">
                                {{ deleteProgressData.node?.name || '-' }}
                            </span>
                        </a-descriptions-item>
                        <a-descriptions-item
                            v-if="deleteProgressData.node?.hierarchy"
                            label="節點路徑"
                            :span="2"
                        >
                            <div class="node-hierarchy-path">
                                <template
                                    v-if="
                                        deleteProgressData.node.hierarchy
                                            .mission
                                    "
                                >
                                    <span class="hierarchy-item">
                                        主線：{{
                                            deleteProgressData.node.hierarchy
                                                .mission.name
                                        }}
                                    </span>
                                </template>
                                <template
                                    v-if="
                                        deleteProgressData.node.hierarchy
                                            .chapter
                                    "
                                >
                                    <span class="hierarchy-item">
                                        章節：{{
                                            deleteProgressData.node.hierarchy
                                                .chapter.name
                                        }}
                                    </span>
                                </template>
                                <template
                                    v-if="
                                        deleteProgressData.node.hierarchy.task
                                    "
                                >
                                    <span class="hierarchy-item">
                                        任務：{{
                                            deleteProgressData.node.hierarchy
                                                .task.name
                                        }}
                                    </span>
                                </template>
                                <template
                                    v-if="
                                        deleteProgressData.node.hierarchy.step
                                    "
                                >
                                    <span class="hierarchy-item">
                                        步驟：{{
                                            deleteProgressData.node.hierarchy
                                                .step.name
                                        }}
                                    </span>
                                </template>
                            </div>
                        </a-descriptions-item>
                        <a-descriptions-item label="狀態">
                            <a-tag
                                :color="
                                    getStatusColor(deleteProgressData.status)
                                "
                            >
                                {{ getStatusLabel(deleteProgressData.status) }}
                            </a-tag>
                        </a-descriptions-item>
                        <a-descriptions-item
                            v-if="deleteProgressData.node?.node_type"
                            label="節點類型"
                        >
                            <a-tag
                                :class="[
                                    'node-type-tag',
                                    `node-type-${deleteProgressData.node.node_type}`
                                ]"
                            >
                                {{
                                    getNodeTypeLabel(
                                        deleteProgressData.node.node_type
                                    )
                                }}
                            </a-tag>
                        </a-descriptions-item>
                        <a-descriptions-item
                            v-if="deleteProgressData.started_at"
                            label="開始時間"
                            :span="2"
                        >
                            {{ formatDateTime(deleteProgressData.started_at) }}
                        </a-descriptions-item>
                        <a-descriptions-item
                            v-if="deleteProgressData.completed_at"
                            label="完成時間"
                            :span="2"
                        >
                            {{
                                formatDateTime(deleteProgressData.completed_at)
                            }}
                        </a-descriptions-item>
                    </a-descriptions>
                </div>

                <!-- 操作提示 -->
                <div class="delete-action-hint">
                    <a-alert
                        type="warning"
                        message="此操作無法復原，請確認後再執行"
                        show-icon
                    />
                </div>
            </div>
        </a-modal>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, reactive, onUnmounted } from 'vue';
import {
    FilterOutlined,
    UnorderedListOutlined,
    ApartmentOutlined,
    InfoCircleOutlined,
    UserOutlined,
    CheckCircleOutlined,
    DownOutlined,
    UpOutlined,
    ClearOutlined,
    MenuUnfoldOutlined,
    SearchOutlined,
    AppstoreOutlined
} from '@ant-design/icons-vue';
import { Popover } from 'ant-design-vue';
import { message } from 'ant-design-vue';
import { showErrorMessage } from '@/utils/messageHelper';
import { handleHttpError } from '@/api/errorHandler';
import dayjs from 'dayjs';
import { applyStartOfDay } from '@/utils/dateTimePicker';
import formatDateTime from '@/utils/formatDateTime';
import DataTable from '@/components/common/DataTable.vue';
import MissionTreeNode from './components/MissionTreeNode.vue';
import {
    getMissionProgress,
    getMissionTreeStats,
    getNodeProgressUsers,
    getMissionDropdown,
    getChapterDropdown,
    getTaskDropdown,
    getStepDropdown,
    getUserMissionProgressDetails,
    updateMissionProgress,
    cancelMissionProgress,
    resetMissionProgress,
    deleteMissionProgress
} from '@/api/missions';
import { searchUsers } from '@/api/users';
import { fetchAllRankRows, toRankOptions } from '@/api/ranks';
import { fetchAllDepartmentRows, toDepartmentOptions } from '@/api/departments';
import { useAdminInfo } from '@/stores/useAdminInfo';
import { useRouter } from 'vue-router';

const adminStore = useAdminInfo();
const router = useRouter();

// 手動控制階層 Popover 的開關，避免閃動
const activeHierarchyPopover = ref(null);
let hierarchyCloseTimer = null;
let isMouseInHierarchyPopover = false;

const openHierarchyPopover = (nodeId) => {
    if (hierarchyCloseTimer) {
        clearTimeout(hierarchyCloseTimer);
        hierarchyCloseTimer = null;
    }
    isMouseInHierarchyPopover = true;
    activeHierarchyPopover.value = nodeId;
};

const closeHierarchyPopover = (nodeId) => {
    isMouseInHierarchyPopover = false;
    if (hierarchyCloseTimer) {
        clearTimeout(hierarchyCloseTimer);
    }
    hierarchyCloseTimer = setTimeout(() => {
        if (
            !isMouseInHierarchyPopover &&
            activeHierarchyPopover.value === nodeId
        ) {
            activeHierarchyPopover.value = null;
        }
        hierarchyCloseTimer = null;
    }, 300);
};

onUnmounted(() => {
    if (hierarchyCloseTimer) {
        clearTimeout(hierarchyCloseTimer);
    }
});

// 權限檢查
const canViewRecord = computed(() => adminStore.hasPermissionCode('130201')); // 查看紀錄
const canEditRecord = computed(() => adminStore.hasPermissionCode('130202')); // 編輯紀錄
const canDeleteRecord = computed(() => adminStore.hasPermissionCode('130203')); // 刪除紀錄
const canViewReview = computed(() => adminStore.hasPermissionCode('130301')); // 查看審核

// 視圖模式：list、tree 或 tabs
const viewMode = ref('list');

// 篩選相關
const filterCollapseActiveKey = ref(['filters']);
const filters = ref({
    user_id: undefined,
    status: undefined,
    identity: undefined,
    rank_id: undefined,
    department_id: undefined
});
const nodePath = ref({
    missionId: undefined,
    chapterId: undefined,
    taskId: undefined,
    stepId: undefined
});
const dateRange = ref(null);

// 使用者搜尋
const userOptions = ref([]);
const userLoading = ref(false);
const userSearchTimer = ref(null);

// 下拉選單選項
const missionOptions = ref([]);
const chapterOptions = ref([]);
const taskOptions = ref([]);
const stepOptions = ref([]);
const loadingMissions = ref(false);
const loadingChapters = ref(false);
const loadingTasks = ref(false);
const loadingSteps = ref(false);

// 使用 computed 轉換選項格式
const missionSelectOptions = computed(() =>
    missionOptions.value.map((mission) => ({
        label: mission.name || `未命名主線（ID: ${mission.node_id}）`,
        value: mission.node_id
    }))
);

const chapterSelectOptions = computed(() =>
    chapterOptions.value.map((chapter) => ({
        label: chapter.name || `未命名章節（ID: ${chapter.node_id}）`,
        value: chapter.node_id
    }))
);

const taskSelectOptions = computed(() =>
    taskOptions.value.map((task) => ({
        label: task.name || `未命名任務（ID: ${task.node_id}）`,
        value: task.node_id
    }))
);

const stepSelectOptions = computed(() =>
    stepOptions.value.map((step) => ({
        label: step.name || `未命名步驟（ID: ${step.node_id}）`,
        value: step.node_id
    }))
);

// 身份、狀態選項
const identityOptions = [
    { label: '訪客', value: 'guest' },
    { label: '學員', value: 'student' },
    { label: '夥伴', value: 'partner' },
    { label: '管理者', value: 'manager' }
];

const statusOptions = [
    { label: '待處理', value: 'pending' },
    { label: '進行中', value: 'in_progress' },
    { label: '已完成', value: 'completed' },
    { label: '失敗', value: 'failed' },
    { label: '已取消', value: 'cancelled' }
];

// 聘階和事業部選項
const rankOptions = ref([]);
const rankLoading = ref(false);
const departmentOptions = ref([]);
const departmentLoading = ref(false);

// 記錄列表相關
const progressList = ref([]);
const progressPagination = ref({
    total: 0,
    current: 1,
    pageSize: 20
});
const loading = ref(false);

// 詳情 Modal
const progressDetailModalVisible = ref(false);
const progressDetailLoading = ref(false);
const progressDetailData = ref(null);
const activeConditionKeys = ref([]);

// 編輯 Modal
const editProgressModalVisible = ref(false);
const editProgressLoading = ref(false);

// 刪除進度記錄 Modal
const deleteModalVisible = ref(false);
const deleteLoading = ref(false);
const deleteProgressData = ref(null);
const currentEditRecord = ref(null);
const editProgressFormRef = ref(null);
const editProgressForm = reactive({
    status: undefined,
    started_at: null,
    completed_at: null,
    note: ''
});
const editProgressFormRules = {
    status: [{ required: false }],
    note: [{ max: 500, message: '備註最多 500 字元' }]
};

// 樹狀統計相關
const treeStatsData = ref([]);

// 標籤頁視圖的狀態
const activeTabKey = ref(null);
const accordionChapterKeys = ref({}); // { missionId: [chapterKeys] }
const accordionTaskKeys = ref({}); // { chapterId: [taskKeys] }

// 獲取章節的展開鍵
const getChapterKeys = (missionId) => {
    return accordionChapterKeys.value[missionId] || [];
};

// 設定章節的展開鍵
const setChapterKeys = (missionId, keys) => {
    accordionChapterKeys.value[missionId] = keys;
};

// 獲取任務的展開鍵
const getTaskKeys = (chapterId) => {
    return accordionTaskKeys.value[chapterId] || [];
};

// 設定任務的展開鍵
const setTaskKeys = (chapterId, keys) => {
    accordionTaskKeys.value[chapterId] = keys;
};

// 檢查 mission 是否展開
const isMissionExpanded = (missionId) => {
    return expandedMissions.value[missionId] === true;
};

// 切換 mission 展開狀態
const toggleMissionExpand = (missionId) => {
    expandedMissions.value[missionId] = !expandedMissions.value[missionId];
};

// 檢查 chapter 是否展開
const isChapterExpanded = (chapterId) => {
    return expandedChapters.value[chapterId] === true;
};

// 切換 chapter 展開狀態
const toggleChapterExpand = (chapterId) => {
    expandedChapters.value[chapterId] = !expandedChapters.value[chapterId];
};

// 檢查 task 是否展開
const isTaskExpanded = (taskId) => {
    return expandedTasks.value[taskId] === true;
};

// 切換 task 展開狀態
const toggleTaskExpand = (taskId) => {
    expandedTasks.value[taskId] = !expandedTasks.value[taskId];
};

// 節點執行者 Modal
const nodeUsersModalVisible = ref(false);
const nodeUsersModalTitle = ref('');
const currentNodeId = ref(null);
const currentNodeUsersData = ref(null);
const nodeUsersList = ref([]);
const nodeUsersPagination = ref({
    total: 0,
    current: 1,
    pageSize: 20
});
const nodeUsersLoading = ref(false);
const nodeUsersSearchText = ref('');
const nodeUsersFilters = ref({
    status: undefined,
    identity: undefined,
    rank_id: undefined,
    department_id: undefined
});

// 表格欄位定義
const progressColumns = computed(() => {
    const columns = [
        {
            title: '姓名',
            key: 'user_name',
            width: 120,
            fixed: 'left'
        },
        {
            title: 'Email',
            key: 'user_email',
            width: 180,
            fixed: 'left'
        },
        {
            title: '身份',
            key: 'user_rank',
            width: 100
        },
        {
            title: '事業部',
            key: 'user_department',
            width: 120
        },
        {
            title: '任務節點',
            key: 'node_hierarchy',
            width: 300
        },
        {
            title: '狀態',
            key: 'status',
            width: 100
        },
        {
            title: '完成進度',
            key: 'conditions_summary',
            width: 150
        },
        {
            title: '開始時間',
            key: 'started_at',
            width: 160
        },
        {
            title: '完成時間',
            key: 'completed_at',
            width: 160
        }
    ];

    // 如果有查看或編輯權限，添加操作欄
    if (canViewRecord.value || canEditRecord.value) {
        columns.push({
            title: '操作',
            key: 'action',
            width: canEditRecord.value ? 150 : 100,
            align: 'center',
            fixed: 'right'
        });
    }

    return columns;
});

const nodeUsersColumns = computed(() => {
    const columns = [
        {
            title: '姓名',
            key: 'user_name',
            width: 150
        },
        {
            title: 'Email',
            key: 'user_email',
            width: 200
        },
        {
            title: '身份',
            key: 'user_rank',
            width: 120
        },
        {
            title: '事業部',
            key: 'user_department',
            width: 150
        },
        {
            title: '狀態',
            key: 'progress_status',
            width: 120
        },
        {
            title: '開始時間',
            key: 'progress_started_at',
            width: 180
        },
        {
            title: '完成時間',
            key: 'progress_completed_at',
            width: 180
        },
        {
            title: '執行次數',
            key: 'attempt_count',
            width: 120
        }
    ];

    // 如果有查看或編輯權限，添加操作欄
    if (canViewRecord.value || canEditRecord.value) {
        columns.push({
            title: '操作',
            key: 'action',
            width: canEditRecord.value ? 150 : 100,
            align: 'center',
            fixed: 'right'
        });
    }

    return columns;
});

// 計算啟用的篩選數量
const getActiveFilterCount = () => {
    let count = 0;
    if (filters.value.user_id) count++;
    if (filters.value.status) count++;
    if (filters.value.identity) count++;
    if (filters.value.rank_id) count++;
    if (filters.value.department_id) count++;
    if (nodePath.value.missionId) count++;
    if (nodePath.value.chapterId) count++;
    if (nodePath.value.taskId) count++;
    if (nodePath.value.stepId) count++;
    if (dateRange.value && dateRange.value.length === 2) count++;
    return count;
};

// 使用者搜尋
const onUserSearch = (searchText) => {
    if (userSearchTimer.value) {
        clearTimeout(userSearchTimer.value);
    }
    userSearchTimer.value = setTimeout(async () => {
        if (!searchText || searchText.trim() === '') {
            userOptions.value = [];
            return;
        }
        userLoading.value = true;
        try {
            const response = await searchUsers({
                name: searchText.trim(),
                limit: 20
            });
            if (response.result && response.data) {
                userOptions.value = response.data.map((user) => ({
                    label: `${user.name || ''} ${user.email || ''}`.trim(),
                    value: user.id
                }));
            }
        } catch (error) {
            console.error('搜尋使用者失敗：', error);
        } finally {
            userLoading.value = false;
        }
    }, 300);
};

// 載入主線選單
const loadMissions = async () => {
    loadingMissions.value = true;
    try {
        const response = await getMissionDropdown();
        if (response?.result) {
            missionOptions.value = response.data || [];
        } else {
            missionOptions.value = [];
        }
    } catch (error) {
        console.error('載入主線選單失敗：', error);
        missionOptions.value = [];
    } finally {
        loadingMissions.value = false;
    }
};

// 載入章節選單
const loadChapters = async (missionId) => {
    if (!missionId) {
        chapterOptions.value = [];
        return;
    }
    loadingChapters.value = true;
    try {
        const response = await getChapterDropdown({
            mission_id: missionId
        });
        if (response?.result) {
            chapterOptions.value = response.data || [];
        } else {
            chapterOptions.value = [];
        }
    } catch (error) {
        console.error('載入章節選單失敗：', error);
        chapterOptions.value = [];
    } finally {
        loadingChapters.value = false;
    }
};

// 載入任務選單
const loadTasks = async (chapterId, missionId) => {
    if (!chapterId && !missionId) {
        taskOptions.value = [];
        return;
    }
    loadingTasks.value = true;
    try {
        const params = {};
        if (missionId) params.mission_id = missionId;
        if (chapterId) params.chapter_id = chapterId;

        const response = await getTaskDropdown(params);
        if (response?.result) {
            taskOptions.value = response.data || [];
        } else {
            taskOptions.value = [];
        }
    } catch (error) {
        console.error('載入任務選單失敗：', error);
        taskOptions.value = [];
    } finally {
        loadingTasks.value = false;
    }
};

// 載入步驟選單
const loadSteps = async (taskId, chapterId, missionId) => {
    if (!taskId && !chapterId && !missionId) {
        stepOptions.value = [];
        return;
    }
    loadingSteps.value = true;
    try {
        const params = {};
        if (missionId) params.mission_id = missionId;
        if (chapterId) params.chapter_id = chapterId;
        if (taskId) params.task_id = taskId;

        const response = await getStepDropdown(params);
        if (response?.result) {
            stepOptions.value = response.data || [];
        } else {
            stepOptions.value = [];
        }
    } catch (error) {
        console.error('載入步驟選單失敗：', error);
        stepOptions.value = [];
    } finally {
        loadingSteps.value = false;
    }
};

// 載入聘階選單
const loadRanks = async () => {
    rankLoading.value = true;
    try {
        const rows = await fetchAllRankRows();
        rankOptions.value = toRankOptions(rows);
    } catch (error) {
        console.error('載入聘階選單失敗：', error);
    } finally {
        rankLoading.value = false;
    }
};

// 載入事業部選單
const loadDepartments = async () => {
    departmentLoading.value = true;
    try {
        const rows = await fetchAllDepartmentRows();
        departmentOptions.value = toDepartmentOptions(rows);
    } catch (error) {
        console.error('載入事業部選單失敗：', error);
    } finally {
        departmentLoading.value = false;
    }
};

// 節點路徑變更處理
const handleMissionChange = async (missionId) => {
    nodePath.value.missionId = missionId;
    nodePath.value.chapterId = undefined;
    nodePath.value.taskId = undefined;
    nodePath.value.stepId = undefined;

    // 清除下層選項
    chapterOptions.value = [];
    taskOptions.value = [];
    stepOptions.value = [];

    // 更新篩選條件
    updateNodeFilters();

    if (missionId) {
        await loadChapters(missionId);
    }
};

const handleChapterChange = async (chapterId) => {
    nodePath.value.chapterId = chapterId;
    nodePath.value.taskId = undefined;
    nodePath.value.stepId = undefined;

    // 清除下層選項
    taskOptions.value = [];
    stepOptions.value = [];

    // 更新篩選條件
    updateNodeFilters();

    if (chapterId) {
        await loadTasks(chapterId, nodePath.value.missionId);
    }
};

const handleTaskChange = async (taskId) => {
    nodePath.value.taskId = taskId;
    nodePath.value.stepId = undefined;

    // 清除下層選項
    stepOptions.value = [];

    // 更新篩選條件
    updateNodeFilters();

    if (taskId) {
        await loadSteps(
            taskId,
            nodePath.value.chapterId,
            nodePath.value.missionId
        );
    }
};

const handleStepChange = (stepId) => {
    nodePath.value.stepId = stepId;
    // 更新篩選條件
    updateNodeFilters();
};

// 更新節點篩選條件
const updateNodeFilters = () => {
    // 清除所有節點篩選條件
    filters.value.mission_id = undefined;
    filters.value.chapter_id = undefined;
    filters.value.task_id = undefined;
    filters.value.step_id = undefined;

    // 根據選擇的路徑設定篩選條件（只設定最底層的 ID）
    if (nodePath.value.stepId) {
        filters.value.step_id = nodePath.value.stepId;
    } else if (nodePath.value.taskId) {
        filters.value.task_id = nodePath.value.taskId;
    } else if (nodePath.value.chapterId) {
        filters.value.chapter_id = nodePath.value.chapterId;
    } else if (nodePath.value.missionId) {
        filters.value.mission_id = nodePath.value.missionId;
    }

    handleFilterChange();
};

const handleNodePathClear = () => {
    nodePath.value = {
        missionId: undefined,
        chapterId: undefined,
        taskId: undefined,
        stepId: undefined
    };
    chapterOptions.value = [];
    taskOptions.value = [];
    stepOptions.value = [];
    updateNodeFilters();
};

// 日期範圍變更
const handleDateRangeChange = () => {
    handleFilterChange();
};

// 篩選變更處理
const handleFilterChange = () => {
    if (viewMode.value === 'list') {
        loadProgressList();
    } else {
        loadTreeStats();
    }
};

// 載入進度記錄列表
const loadProgressList = async () => {
    loading.value = true;
    try {
        const params = {
            page: progressPagination.value.current,
            per_page: progressPagination.value.pageSize
        };

        // 基本篩選
        if (filters.value.user_id) {
            params.user_id = filters.value.user_id;
        }
        if (filters.value.status) {
            params.status = filters.value.status;
        }
        if (filters.value.identity) {
            params.identity = filters.value.identity;
        }
        if (filters.value.rank_id) {
            params.rank_id = filters.value.rank_id;
        }
        if (filters.value.department_id) {
            params.department_id = filters.value.department_id;
        }

        // 節點路徑篩選（使用 filters 中的值，由 updateNodeFilters 設定）
        if (filters.value.step_id) {
            params.step_id = filters.value.step_id;
        } else if (filters.value.task_id) {
            params.task_id = filters.value.task_id;
        } else if (filters.value.chapter_id) {
            params.chapter_id = filters.value.chapter_id;
        } else if (filters.value.mission_id) {
            params.mission_id = filters.value.mission_id;
        }

        // 日期範圍
        if (dateRange.value && dateRange.value.length === 2) {
            const startDate = dateRange.value[0];
            const endDate = dateRange.value[1];
            if (startDate && endDate) {
                // 確保是 dayjs 物件
                if (typeof startDate.format === 'function') {
                    params.start_date = startDate.format('YYYY-MM-DD');
                } else if (startDate instanceof Date) {
                    params.start_date = dayjs(startDate).format('YYYY-MM-DD');
                }
                if (typeof endDate.format === 'function') {
                    params.end_date = endDate.format('YYYY-MM-DD');
                } else if (endDate instanceof Date) {
                    params.end_date = dayjs(endDate).format('YYYY-MM-DD');
                }
            }
        }

        const response = await getMissionProgress(params);
        if (response.result && response.data) {
            progressList.value = response.data.data || [];
            if (response.data.pagination) {
                progressPagination.value = {
                    total: response.data.pagination.total || 0,
                    current: response.data.pagination.current_page || 1,
                    pageSize: response.data.pagination.per_page || 20
                };
            }
        } else {
            // API 回傳失敗
            const errorMsg = response.message || '載入進度記錄失敗';
            showErrorMessage(errorMsg);
        }
    } catch (error) {
        console.error('載入進度記錄失敗：', error);

        // 處理網路錯誤（如 Failed to fetch）
        if (error.message && error.message.includes('fetch')) {
            showErrorMessage('網路連線失敗，請檢查網路連線或稍後再試');
            return;
        }

        // 處理 HTTP 錯誤
        if (error.response) {
            handleHttpError(error, '/api/admin/mission-progress', 'GET', {
                skipNotification: true
            });
            const errorMsg =
                error.response?.data?.message ||
                error.message ||
                '載入進度記錄失敗';
            showErrorMessage(errorMsg);
        } else if (error.message) {
            // 其他類型的錯誤（如網路錯誤、超時等）
            showErrorMessage(error.message || '載入進度記錄失敗');
        } else {
            showErrorMessage('載入進度記錄失敗');
        }
    } finally {
        loading.value = false;
    }
};

// 載入樹狀統計
const loadTreeStats = async () => {
    loading.value = true;
    try {
        const params = {};

        // 基本篩選
        if (filters.value.identity) {
            params.identity = filters.value.identity;
        }
        if (filters.value.rank_id) {
            params.rank_id = filters.value.rank_id;
        }
        if (filters.value.department_id) {
            params.department_id = filters.value.department_id;
        }
        if (filters.value.status) {
            params.status = filters.value.status;
        }

        // 節點路徑篩選（使用 filters 中的值）
        if (filters.value.mission_id) {
            params.mission_id = filters.value.mission_id;
        }

        // 日期範圍
        if (dateRange.value && dateRange.value.length === 2) {
            const startDate = dateRange.value[0];
            const endDate = dateRange.value[1];
            if (startDate && endDate) {
                // 確保是 dayjs 物件
                if (typeof startDate.format === 'function') {
                    params.start_date = startDate.format('YYYY-MM-DD');
                } else if (startDate instanceof Date) {
                    params.start_date = dayjs(startDate).format('YYYY-MM-DD');
                }
                if (typeof endDate.format === 'function') {
                    params.end_date = endDate.format('YYYY-MM-DD');
                } else if (endDate instanceof Date) {
                    params.end_date = dayjs(endDate).format('YYYY-MM-DD');
                }
            }
        }

        const response = await getMissionTreeStats(params);
        if (response.result && response.data) {
            treeStatsData.value = response.data.missions || [];
            // 標籤頁視圖預設選第一個
            if (viewMode.value === 'tabs' && treeStatsData.value.length > 0) {
                activeTabKey.value = `mission-${treeStatsData.value[0].node.id}`;
                accordionChapterKeys.value = {};
                accordionTaskKeys.value = {};
            }
        } else {
            // API 回傳失敗
            const errorMsg = response.message || '載入樹狀統計失敗';
            showErrorMessage(errorMsg);
        }
    } catch (error) {
        console.error('載入樹狀統計失敗：', error);

        // 處理網路錯誤（如 Failed to fetch）
        if (error.message && error.message.includes('fetch')) {
            showErrorMessage('網路連線失敗，請檢查網路連線或稍後再試');
            return;
        }

        // 處理 HTTP 錯誤
        if (error.response) {
            handleHttpError(
                error,
                '/api/admin/mission-progress/tree-stats',
                'GET',
                { skipNotification: true }
            );
            const errorMsg =
                error.response?.data?.message ||
                error.message ||
                '載入樹狀統計失敗';
            showErrorMessage(errorMsg);
        } else if (error.message) {
            // 其他類型的錯誤（如網路錯誤、超時等）
            showErrorMessage(error.message || '載入樹狀統計失敗');
        } else {
            showErrorMessage('載入樹狀統計失敗');
        }
    } finally {
        loading.value = false;
    }
};

// 載入節點執行者列表
const loadNodeUsers = async () => {
    if (!currentNodeId.value) return;
    nodeUsersLoading.value = true;
    try {
        const params = {
            page: nodeUsersPagination.value.current,
            per_page: nodeUsersPagination.value.pageSize
        };

        // 搜尋條件
        if (nodeUsersSearchText.value?.trim()) {
            params.search = nodeUsersSearchText.value.trim();
        }

        // 篩選條件
        if (nodeUsersFilters.value.status) {
            params.status = nodeUsersFilters.value.status;
        }
        if (nodeUsersFilters.value.identity) {
            params.identity = nodeUsersFilters.value.identity;
        }
        if (nodeUsersFilters.value.rank_id) {
            params.rank_id = nodeUsersFilters.value.rank_id;
        }
        if (nodeUsersFilters.value.department_id) {
            params.department_id = nodeUsersFilters.value.department_id;
        }

        const response = await getNodeProgressUsers(
            currentNodeId.value,
            params
        );
        if (response.result && response.data) {
            currentNodeUsersData.value = {
                node: response.data.node,
                stats: response.data.stats
            };
            nodeUsersList.value = response.data.users || [];
            if (response.data.pagination) {
                nodeUsersPagination.value = {
                    total: response.data.pagination.total || 0,
                    current: response.data.pagination.current_page || 1,
                    pageSize: response.data.pagination.per_page || 20
                };
            }
        }
    } catch (error) {
        console.error('載入節點執行者列表失敗：', error);
        showErrorMessage('載入節點執行者列表失敗');
    } finally {
        nodeUsersLoading.value = false;
    }
};

// 查看節點執行者
const handleViewNodeUsers = (nodeId) => {
    currentNodeId.value = nodeId;
    nodeUsersModalVisible.value = true;
    nodeUsersModalTitle.value = '參與者列表';
    nodeUsersPagination.value.current = 1;
    // 初始化資料，避免視窗內容不顯示
    currentNodeUsersData.value = {
        node: null,
        stats: null
    };
    nodeUsersList.value = [];
    loadNodeUsers();
};

// 關閉節點執行者 Modal
const handleNodeUsersModalCancel = () => {
    nodeUsersModalVisible.value = false;
    currentNodeId.value = null;
    currentNodeUsersData.value = null;
    nodeUsersList.value = [];
    nodeUsersSearchText.value = '';
    nodeUsersFilters.value = {
        status: undefined,
        identity: undefined,
        rank_id: undefined,
        department_id: undefined
    };
};

// 節點參與者搜尋
const handleNodeUsersSearch = () => {
    nodeUsersPagination.value.current = 1;
    loadNodeUsers();
};

// 節點參與者篩選變更
const handleNodeUsersFilterChange = () => {
    nodeUsersPagination.value.current = 1;
    loadNodeUsers();
};

// 跳轉到審核列表（過濾特定記錄）
const handleGoToReviews = (record) => {
    if (!canViewReview.value) {
        message.warning('您沒有查看審核的權限');
        return;
    }
    // 跳轉到審核列表，並帶上篩選條件以過濾出該筆記錄的待審核項目
    const query = {
        result_status: 'pending'
    };

    // 添加用戶名稱過濾（而不是 user_id）
    if (record.user?.name) {
        query.user_name = record.user.name;
    }

    // 添加任務節點篩選條件
    if (record.node_hierarchy?.step?.id) {
        query.step_id = record.node_hierarchy.step.id;
    } else if (record.node_hierarchy?.task?.id) {
        query.task_id = record.node_hierarchy.task.id;
    } else if (record.node_hierarchy?.chapter?.id) {
        query.chapter_id = record.node_hierarchy.chapter.id;
    } else if (record.node_hierarchy?.mission?.id) {
        query.mission_id = record.node_hierarchy.mission.id;
    }

    router.push({
        path: '/missions/mission-reviews',
        query
    });
};

// 查看進度記錄詳情
const handleViewProgressDetail = async (record) => {
    if (!canViewRecord.value) {
        message.warning('您沒有查看紀錄的權限');
        return;
    }

    if (!record.user?.id || !record.id) {
        showErrorMessage('無法取得記錄詳情：缺少必要資訊');
        return;
    }

    progressDetailModalVisible.value = true;
    progressDetailLoading.value = true;
    progressDetailData.value = null;
    activeConditionKeys.value = [];

    try {
        const response = await getUserMissionProgressDetails(
            record.user.id,
            record.id
        );
        if (response?.result && response.data) {
            progressDetailData.value = response.data;
            // 預設展開第一個條件
            if (
                response.data.conditions &&
                response.data.conditions.length > 0
            ) {
                activeConditionKeys.value = [0];
            }
        } else {
            showErrorMessage(response?.message || '取得記錄詳情失敗');
            progressDetailModalVisible.value = false;
        }
    } catch (error) {
        console.error('取得進度記錄詳情失敗：', error);
        showErrorMessage('取得記錄詳情失敗');
        progressDetailModalVisible.value = false;
    } finally {
        progressDetailLoading.value = false;
    }
};

// 翻譯條件 code
const translateConditionCode = (code) => {
    if (!code) return '-';

    // 移除後綴的數字（如 quiz_pass_1 -> quiz_pass）
    const codeBase = code.replace(/_\d+$/, '');

    const codeMap = {
        quiz_pass: '測驗',
        quiz: '測驗',
        form: '表單',
        file_upload: '檔案上傳',
        payment: '付款',
        purchase: '購買商品',
        sign: '簽名',
        location: '位置',
        qr_code: 'QR Code',
        nfc: 'NFC',
        manual: '手動',
        auto: '自動'
    };

    return codeMap[codeBase] || code;
};

// 取得條件顯示名稱
const getConditionDisplayName = (condition) => {
    if (condition.code_name) {
        return condition.code_name;
    }
    if (condition.code) {
        return translateConditionCode(condition.code);
    }
    return '條件';
};

// 格式化 metadata 標籤
const formatMetadataLabel = (key) => {
    const labelMap = {
        manual_completed: '手動完成',
        manual_completed_at: '手動完成時間',
        manual_completed_by: '手動完成人員 ID',
        manual_completed_by_name: '手動完成人員',
        manual_completed_note: '手動完成備註',
        quiz_id: '測驗 ID',
        quiz_name: '測驗名稱',
        attempt_id: '作答紀錄 ID',
        verified_at: '驗證時間',
        auto_verified: '自動驗證',
        status: '狀態',
        product_id: '商品 ID',
        product_name: '商品名稱'
    };
    return (
        labelMap[key] ||
        key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
    );
};

// 決定 metadata 欄位的 span
const getMetadataSpan = (key) => {
    // 備註類的欄位佔兩列
    if (
        key.includes('note') ||
        key.includes('description') ||
        key.includes('message')
    ) {
        return 2;
    }
    return 1;
};

// 編輯進度記錄
const handleEditProgress = (record) => {
    if (!canEditRecord.value) {
        message.warning('您沒有編輯紀錄的權限');
        return;
    }

    if (!record.user?.id || !record.id) {
        showErrorMessage('無法取得記錄資訊：缺少必要資訊');
        return;
    }

    currentEditRecord.value = record;
    editProgressForm.status = record.status;
    editProgressForm.started_at = record.started_at
        ? dayjs(record.started_at)
        : null;
    editProgressForm.completed_at = record.completed_at
        ? dayjs(record.completed_at)
        : null;
    editProgressForm.note = '';
    editProgressModalVisible.value = true;
};

// 提交編輯
const handleSubmitEditProgress = async () => {
    if (!canEditRecord.value) {
        message.warning('您沒有編輯紀錄的權限');
        return;
    }

    try {
        await editProgressFormRef.value.validate();
    } catch (error) {
        return;
    }

    if (!currentEditRecord.value?.user?.id || !currentEditRecord.value?.id) {
        showErrorMessage('無法取得記錄資訊');
        return;
    }

    editProgressLoading.value = true;

    try {
        const submitData = {};
        if (editProgressForm.status) {
            submitData.status = editProgressForm.status;
        }
        if (editProgressForm.started_at) {
            submitData.started_at = editProgressForm.started_at.format(
                'YYYY-MM-DD HH:mm:ss'
            );
        } else {
            submitData.started_at = null;
        }
        if (editProgressForm.completed_at) {
            submitData.completed_at = editProgressForm.completed_at.format(
                'YYYY-MM-DD HH:mm:ss'
            );
        } else {
            submitData.completed_at = null;
        }
        if (editProgressForm.note) {
            submitData.note = editProgressForm.note;
        }

        const response = await updateMissionProgress(
            currentEditRecord.value.user.id,
            currentEditRecord.value.id,
            submitData
        );

        if (response?.result) {
            message.success('編輯成功');
            editProgressModalVisible.value = false;
            loadData();
        } else {
            showErrorMessage(response?.message || '編輯失敗');
        }
    } catch (error) {
        console.error('編輯進度記錄失敗：', error);
        showErrorMessage('編輯失敗');
    } finally {
        editProgressLoading.value = false;
    }
};

// 取消進度
const handleCancelProgress = async () => {
    if (!canEditRecord.value) {
        message.warning('您沒有編輯紀錄的權限');
        return;
    }

    if (!currentEditRecord.value?.user?.id || !currentEditRecord.value?.id) {
        showErrorMessage('無法取得記錄資訊');
        return;
    }

    // 檢查狀態是否可以取消
    if (
        currentEditRecord.value.status !== 'pending' &&
        currentEditRecord.value.status !== 'in_progress'
    ) {
        message.warning('只能取消待處理或進行中的任務進度');
        return;
    }

    editProgressLoading.value = true;

    try {
        const submitData = {};
        if (editProgressForm.note) {
            submitData.note = editProgressForm.note;
        }

        const response = await cancelMissionProgress(
            currentEditRecord.value.user.id,
            currentEditRecord.value.id,
            submitData
        );

        if (response?.result) {
            message.success('取消成功');
            editProgressModalVisible.value = false;
            loadData();
        } else {
            showErrorMessage(response?.message || '取消失敗');
        }
    } catch (error) {
        console.error('取消進度記錄失敗：', error);
        showErrorMessage('取消失敗');
    } finally {
        editProgressLoading.value = false;
    }
};

// 重置進度
const handleResetProgress = async () => {
    if (!canEditRecord.value) {
        message.warning('您沒有編輯紀錄的權限');
        return;
    }

    if (!currentEditRecord.value?.user?.id || !currentEditRecord.value?.id) {
        showErrorMessage('無法取得記錄資訊');
        return;
    }

    // 檢查狀態是否可以重置
    if (
        currentEditRecord.value.status !== 'completed' &&
        currentEditRecord.value.status !== 'failed' &&
        currentEditRecord.value.status !== 'cancelled'
    ) {
        message.warning('只能重置已完成、失敗或已取消的任務進度');
        return;
    }

    editProgressLoading.value = true;

    try {
        const submitData = {
            reset_to: 'pending',
            reset_conditions: false
        };
        if (editProgressForm.note) {
            submitData.note = editProgressForm.note;
        }

        const response = await resetMissionProgress(
            currentEditRecord.value.user.id,
            currentEditRecord.value.id,
            submitData
        );

        if (response?.result) {
            message.success('重置成功');
            editProgressModalVisible.value = false;
            loadData();
        } else {
            showErrorMessage(response?.message || '重置失敗');
        }
    } catch (error) {
        console.error('重置進度記錄失敗：', error);
        showErrorMessage('重置失敗');
    } finally {
        editProgressLoading.value = false;
    }
};

// 開啟刪除進度記錄 Modal
const handleDeleteProgress = (record) => {
    if (!canDeleteRecord.value) {
        message.warning('您沒有刪除紀錄的權限');
        return;
    }

    if (!record?.user?.id || !record?.id) {
        showErrorMessage('無法取得記錄資訊：缺少必要資訊');
        return;
    }

    // 檢查狀態是否可以刪除（非完成狀態）
    if (record.status === 'completed') {
        message.warning('無法刪除已完成的任務進度');
        return;
    }

    deleteProgressData.value = record;
    deleteModalVisible.value = true;
};

// 確認刪除進度記錄
const handleConfirmDelete = async () => {
    if (!canDeleteRecord.value) {
        message.warning('您沒有刪除紀錄的權限');
        return;
    }

    if (!deleteProgressData.value) return;

    if (!deleteProgressData.value.user?.id || !deleteProgressData.value.id) {
        showErrorMessage('無法取得記錄資訊：缺少必要資訊');
        return;
    }

    deleteLoading.value = true;

    try {
        const response = await deleteMissionProgress(
            deleteProgressData.value.user.id,
            deleteProgressData.value.id
        );

        if (response?.result || response?.success) {
            message.success('刪除成功');
            deleteModalVisible.value = false;
            deleteProgressData.value = null;
            loadData();
        } else {
            showErrorMessage(response?.message || '刪除失敗');
        }
    } catch (error) {
        console.error('刪除進度記錄失敗：', error);
        showErrorMessage('刪除失敗');
    } finally {
        deleteLoading.value = false;
    }
};

// 表格變更處理
const handleProgressTableChange = ({ pagination }) => {
    if (pagination) {
        progressPagination.value.current = pagination.current;
        progressPagination.value.pageSize = pagination.pageSize;
    }
    loadProgressList();
};

const handleNodeUsersTableChange = ({ pagination }) => {
    if (pagination) {
        nodeUsersPagination.value.current = pagination.current;
        nodeUsersPagination.value.pageSize = pagination.pageSize;
    }
    loadNodeUsers();
};

// 統一載入資料
const loadData = () => {
    if (viewMode.value === 'list') {
        loadProgressList();
    } else {
        loadTreeStats();
        // 標籤頁視圖預設選第一個
        if (viewMode.value === 'tabs' && treeStatsData.value.length > 0) {
            activeTabKey.value = `mission-${treeStatsData.value[0].node.id}`;
            accordionChapterKeys.value = {};
            accordionTaskKeys.value = {};
        }
    }
};

// 重置所有篩選條件
const handleReset = () => {
    // 重置基本篩選
    filters.value = {
        user_id: undefined,
        status: undefined,
        identity: undefined,
        rank_id: undefined,
        department_id: undefined
    };

    // 重置節點路徑
    nodePath.value = {
        missionId: undefined,
        chapterId: undefined,
        taskId: undefined,
        stepId: undefined
    };

    // 重置日期範圍
    dateRange.value = null;

    // 清除下拉選單選項
    chapterOptions.value = [];
    taskOptions.value = [];
    stepOptions.value = [];
    userOptions.value = [];

    // 重新載入資料
    loadData();
};

// 狀態標籤和顏色
const getStatusLabel = (status) => {
    const statusMap = {
        in_progress: '進行中',
        completed: '已完成',
        failed: '失敗',
        cancelled: '已取消',
        pending: '待處理'
    };
    return statusMap[status] || status || '-';
};

const getStatusColor = (status) => {
    const colorMap = {
        in_progress: 'processing',
        completed: 'success',
        failed: 'error',
        cancelled: 'volcano',
        pending: 'warning'
    };
    return colorMap[status] || 'default';
};

// 節點類型標籤和顏色
const getNodeTypeLabel = (nodeType) => {
    const typeMap = {
        mission: '主線',
        chapter: '章節',
        task: '任務',
        step: '步驟'
    };
    return typeMap[nodeType] || nodeType || '-';
};

const getNodeTypeColor = (nodeType) => {
    const colorMap = {
        mission: 'purple',
        chapter: 'cyan',
        task: 'blue',
        step: 'green'
    };
    return colorMap[nodeType] || 'default';
};

// 節點類型顏色已改為使用 CSS class，不再需要此函數

// 點擊處理函數
const handleUserClick = (e, user) => {
    e?.stopPropagation();
    if (user?.id) {
        // 確保該用戶在 userOptions 中，以便顯示名稱
        const existingOption = userOptions.value.find(
            (opt) => opt.value === user.id
        );
        if (!existingOption) {
            userOptions.value.push({
                label:
                    `${user.name || ''} ${user.email || ''}`.trim() ||
                    `用戶 #${user.id}`,
                value: user.id
            });
        }
        filters.value.user_id = user.id;
        handleFilterChange();
    }
};

const handleRankClick = (e, rank) => {
    e?.stopPropagation();
    if (rank?.id) {
        // 確保該聘階在 rankOptions 中
        const existingOption = rankOptions.value.find(
            (opt) => opt.value === Number(rank.id)
        );
        if (!existingOption && rank.name) {
            rankOptions.value.push({
                label: rank.name,
                value: Number(rank.id)
            });
        }
        filters.value.rank_id = Number(rank.id);
        handleFilterChange();
    }
};

const handleDepartmentClick = (e, department) => {
    e?.stopPropagation();
    if (department?.id) {
        // 確保該事業部在 departmentOptions 中
        const existingOption = departmentOptions.value.find(
            (opt) => opt.value === Number(department.id)
        );
        if (!existingOption && department.name) {
            departmentOptions.value.push({
                label: department.name,
                value: Number(department.id)
            });
        }
        filters.value.department_id = Number(department.id);
        handleFilterChange();
    }
};

const handleNodeClick = async (e, node) => {
    e?.stopPropagation();
    if (!node?.id || !node?.hierarchy) return;

    const hierarchy = node.hierarchy;

    // 根據節點類型設定對應的 filter
    if (hierarchy.step?.id) {
        nodePath.value.stepId = hierarchy.step.id;
        nodePath.value.taskId = hierarchy.task?.id;
        nodePath.value.chapterId = hierarchy.chapter?.id;
        nodePath.value.missionId = hierarchy.mission?.id;
    } else if (hierarchy.task?.id) {
        nodePath.value.taskId = hierarchy.task.id;
        nodePath.value.chapterId = hierarchy.chapter?.id;
        nodePath.value.missionId = hierarchy.mission?.id;
        nodePath.value.stepId = undefined;
    } else if (hierarchy.chapter?.id) {
        nodePath.value.chapterId = hierarchy.chapter.id;
        nodePath.value.missionId = hierarchy.mission?.id;
        nodePath.value.taskId = undefined;
        nodePath.value.stepId = undefined;
    } else if (hierarchy.mission?.id) {
        nodePath.value.missionId = hierarchy.mission.id;
        nodePath.value.chapterId = undefined;
        nodePath.value.taskId = undefined;
        nodePath.value.stepId = undefined;
    }

    // 載入對應的下拉選單選項，確保能顯示名稱
    if (nodePath.value.missionId) {
        await loadMissions();
        await loadChapters(nodePath.value.missionId);
    }
    if (nodePath.value.chapterId) {
        await loadTasks(nodePath.value.chapterId, nodePath.value.missionId);
    }
    if (nodePath.value.taskId) {
        await loadSteps(
            nodePath.value.taskId,
            nodePath.value.chapterId,
            nodePath.value.missionId
        );
    }

    updateNodeFilters();
};

// 監聽視圖模式切換
watch(viewMode, (newMode) => {
    loadData();
});

// 初始化
onMounted(() => {
    // 檢查權限
    if (!canViewRecord.value) {
        message.warning('您沒有查看紀錄的權限');
        return;
    }

    loadMissions();
    loadRanks();
    loadDepartments();
    loadData();
});
</script>

<style scoped>
.missions-records-page {
    padding: 32px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.98),
        rgba(248, 249, 250, 0.95),
        rgba(255, 255, 255, 0.98)
    );
    min-height: 100vh;
    position: relative;
    overflow-x: hidden;
}

/* 背景裝飾網格 */
.missions-records-page::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image:
        linear-gradient(rgba(42, 74, 122, 0.03) 1px, transparent 1px),
        linear-gradient(90deg, rgba(42, 74, 122, 0.03) 1px, transparent 1px);
    background-size: 40px 40px;
    pointer-events: none;
    z-index: 0;
}

.page-header {
    margin-bottom: 20px;
    position: relative;
    z-index: 1;
}

.page-header h1 {
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 8px 0;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.page-header p {
    margin: 0;
    color: #6a6a7a;
    font-size: 14px;
    font-family: 'Courier New', monospace;
}

.content {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    border: 2px solid transparent;
    border-radius: 16px;
    padding: 32px;
    min-height: 400px;
    position: relative;
    box-shadow: 0 0 30px rgba(42, 74, 122, 0.3);
    z-index: 1;
}

.content::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 16px;
    padding: 2px;
    background: linear-gradient(45deg, #64748b, #2a4a7a, #3b6ea5);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask-composite: xor;
}

/* 工具欄樣式 */
.toolbar {
    margin-bottom: 24px;
    position: relative;
    z-index: 2;
}

.filter-collapse {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    border: 1px solid rgba(42, 74, 122, 0.2);
    border-radius: 16px;
    box-shadow:
        0 4px 16px rgba(42, 74, 122, 0.1),
        inset 0 1px 0 rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
    overflow: hidden;
    margin-bottom: 16px;
}

.filter-collapse :deep(.ant-collapse-item) {
    border: none;
    background: transparent;
}

.filter-collapse :deep(.ant-collapse-header) {
    padding: 16px 24px;
    background: transparent;
    border-bottom: 1px solid rgba(42, 74, 122, 0.1);
}

.filter-collapse :deep(.ant-collapse-content) {
    background: transparent;
    border: none;
}

.filter-collapse :deep(.ant-collapse-content-box) {
    padding: 20px 24px;
}

.filter-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
}

.filter-header-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.filters-content {
    padding: 0;
}

.filter-group {
    margin-bottom: 20px;
}

.filter-group:last-child {
    margin-bottom: 0;
}

.filter-group-title {
    font-weight: 600;
    color: #5a6578;
    margin-bottom: 12px;
    font-family: 'Courier New', monospace;
    font-size: 14px;
}

.filter-row {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
}

.filter-item {
    flex: 1;
    min-width: 180px;
}

.toolbar-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
}

.view-mode-legend {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 12px;
    background: rgba(255, 255, 255, 0.6);
    border: 1px solid rgba(42, 74, 122, 0.2);
    border-radius: 8px;
    font-size: 12px;
    font-family: 'Courier New', monospace;
    color: #6a6a7a;
}

.legend-icon {
    color: #2a4a7a;
    font-size: 14px;
}

.legend-icon-small {
    font-size: 12px;
    color: #6a6a7a;
    display: inline-flex;
    align-items: center;
}

.legend-icon-small.legend-icon-success {
    color: #52c41a;
}

.legend-text {
    display: flex;
    align-items: center;
    gap: 4px;
}

.legend-dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
}

.legend-dot.completed {
    background: #52c41a;
    box-shadow: 0 0 3px rgba(82, 196, 26, 0.4);
}

.legend-dot.processing {
    background: #1890ff;
    box-shadow: 0 0 3px rgba(24, 144, 255, 0.4);
}

.legend-dot.error {
    background: #ff4d4f;
    box-shadow: 0 0 3px rgba(255, 77, 79, 0.4);
}

.legend-divider {
    color: #d0d0d0;
    margin: 0 4px;
}

.view-mode-switch {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    border: 2px solid rgba(42, 74, 122, 0.25);
    border-radius: 10px;
    padding: 4px;
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.15);
}

.view-mode-switch :deep(.ant-radio-button-wrapper) {
    border: none !important;
    background: transparent !important;
    color: #2a2a3a !important;
    font-family: 'Courier New', monospace;
    font-weight: 500;
    transition: all 0.3s ease;
    border-radius: 7px;
    padding: 4px 12px;
    height: auto;
    line-height: 1.5;
}

.view-mode-switch :deep(.ant-radio-button-wrapper:hover) {
    color: #2a4a7a !important;
    background: rgba(42, 74, 122, 0.1) !important;
}

.view-mode-switch :deep(.ant-radio-button-wrapper-checked) {
    background: linear-gradient(45deg, #2a4a7a, #3b6ea5) !important;
    color: #ffffff !important;
    box-shadow: 0 0 15px rgba(42, 74, 122, 0.3);
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
}

.view-mode-switch :deep(.ant-radio-button-wrapper-checked:hover) {
    background: linear-gradient(45deg, #1a4268, #3b6ea5) !important;
}

/* 蒸氣波風格按鈕 */
.vaporwave-btn {
    border-radius: 8px;
    font-family: 'Courier New', monospace;
    font-weight: 600;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    border: none;
    box-shadow:
        0 4px 16px rgba(42, 74, 122, 0.25),
        inset 0 0 12px rgba(255, 255, 255, 0.1);
    text-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
    height: 36px;
    padding: 0 20px;
    font-size: 14px;
}

.vaporwave-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
        90deg,
        transparent,
        rgba(255, 255, 255, 0.3),
        transparent
    );
    transition: left 0.5s ease;
}

.vaporwave-btn:hover::before {
    left: 100%;
}

.vaporwave-btn:hover {
    transform: translateY(-2px) scale(1.02);
    box-shadow:
        0 6px 24px rgba(42, 74, 122, 0.4),
        inset 0 0 15px rgba(255, 255, 255, 0.15);
}

.vaporwave-btn:active {
    transform: translateY(0) scale(1);
    box-shadow:
        0 2px 10px rgba(42, 74, 122, 0.3),
        inset 0 0 8px rgba(255, 255, 255, 0.1);
}

.vaporwave-btn.ant-btn-primary {
    background: linear-gradient(45deg, #2a4a7a, #3b6ea5) !important;
    color: #ffffff !important;
}

.vaporwave-btn.ant-btn-primary:hover {
    background: linear-gradient(45deg, #1a4268, #3b6ea5) !important;
}

.reset-btn {
    background: rgba(255, 255, 255, 0.9) !important;
    border: 1px solid rgba(42, 74, 122, 0.3) !important;
    color: #2a4a7a !important;
}

.reset-btn:hover {
    background: rgba(255, 255, 255, 1) !important;
    border-color: #2a4a7a !important;
    color: #3b6ea5 !important;
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.2) !important;
}

/* 記錄列表視圖 */
.records-list-view {
    margin-top: 24px;
}

/* 樹狀統計視圖 */
.tree-stats-view {
    margin-top: 24px;
}

.loading-container,
.empty-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 300px;
}

.tree-stats-content {
    margin-top: 0;
}

/* 主線卡片網格佈局 - 改為單列列表式 */
.missions-grid {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

/* 外層卡片 - 改為更緊湊的樣式 */
.mission-wrapper-card {
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(42, 74, 122, 0.2);
    border-left: 4px solid #2a4a7a;
    border-radius: 8px;
    padding: 12px;
    transition: all 0.2s ease;
    box-shadow: 0 1px 4px rgba(42, 74, 122, 0.08);
    position: relative;
}

.mission-wrapper-card:hover {
    border-color: rgba(42, 74, 122, 0.35);
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.15);
    background: rgba(255, 255, 255, 0.95);
}

/* 主線卡片（固定顯示） */
.mission-header-card {
    position: relative;
    z-index: 1;
    padding: 0;
    min-height: 80px;
}

.mission-header-card :deep(.node-card) {
    margin-bottom: 0;
}

.expand-toggle-btn {
    position: absolute;
    bottom: 12px;
    right: 12px;
    color: #2a4a7a;
    font-size: 12px;
    padding: 4px 12px;
    border-radius: 4px;
    transition: all 0.2s;
    font-family: 'Courier New', monospace;
    height: auto;
    line-height: 1.5;
    z-index: 10;
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(42, 74, 122, 0.3);
}

.expand-toggle-btn:hover {
    background: rgba(42, 74, 122, 0.1);
    color: #3b6ea5;
    border-color: rgba(42, 74, 122, 0.5);
}

/* 分割線 - 移除或簡化 */
.mission-divider {
    display: none;
}

/* 章節內容區域 */
.mission-content {
    position: relative;
    z-index: 1;
    padding-top: 8px;
    margin-top: 8px;
    padding-left: 20px;
    border-left: 2px solid rgba(42, 74, 122, 0.15);
}

/* 章節包裝器 */
.chapter-wrapper {
    margin-bottom: 8px;
}

.chapter-header-card {
    position: relative;
    z-index: 1;
    background: rgba(255, 255, 255, 0.7);
    border: 1px solid rgba(138, 175, 255, 0.2);
    border-left: 3px solid rgba(138, 175, 255, 0.4);
    border-radius: 6px;
    padding: 10px;
    margin-bottom: 6px;
    min-height: 70px;
}

.chapter-header-card :deep(.node-card) {
    margin-bottom: 0;
    padding: 12px;
}

.chapter-divider {
    display: none;
}

.chapter-content {
    padding-left: 20px;
    margin-top: 6px;
    border-left: 2px solid rgba(138, 175, 255, 0.15);
}

/* 任務包裝器 */
.task-wrapper {
    margin-bottom: 12px;
}

.task-header-card {
    position: relative;
    z-index: 1;
    background: rgba(255, 255, 255, 0.6);
    border: 1px solid rgba(168, 255, 246, 0.2);
    border-left: 3px solid rgba(168, 255, 246, 0.4);
    border-radius: 6px;
    padding: 8px;
    margin-bottom: 6px;
    min-height: 65px;
}

.task-header-card :deep(.node-card) {
    margin-bottom: 0;
    padding: 10px;
}

.task-divider {
    display: none;
}

.task-content {
    padding-left: 20px;
    margin-top: 6px;
    border-left: 2px solid rgba(168, 255, 246, 0.15);
}

/* 標籤頁視圖樣式 */
.tabs-stats-view {
    margin-top: 24px;
}

.tabs-stats-content {
    margin-top: 0;
}

.mission-tabs :deep(.ant-tabs-nav) {
    margin-bottom: 20px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    padding: 12px;
    border-radius: 12px;
    box-shadow: 0 4px 16px rgba(42, 74, 122, 0.15);
    border: 1px solid rgba(42, 74, 122, 0.2);
}

.mission-tabs :deep(.ant-tabs-tab) {
    border: 1px solid rgba(42, 74, 122, 0.25);
    border-radius: 8px;
    margin-right: 10px;
    padding: 10px 20px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.9),
        rgba(248, 249, 250, 0.8)
    );
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
}

.mission-tabs :deep(.ant-tabs-tab::before) {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
        90deg,
        transparent,
        rgba(42, 74, 122, 0.1),
        transparent
    );
    transition: left 0.5s ease;
}

.mission-tabs :deep(.ant-tabs-tab:hover::before) {
    left: 100%;
}

.mission-tabs :deep(.ant-tabs-tab:hover) {
    background: linear-gradient(
        135deg,
        rgba(42, 74, 122, 0.15),
        rgba(138, 175, 255, 0.1)
    );
    border-color: rgba(42, 74, 122, 0.5);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(42, 74, 122, 0.2);
}

.mission-tabs :deep(.ant-tabs-tab-active) {
    background: linear-gradient(45deg, #2a4a7a, #3b6ea5);
    border-color: #2a4a7a;
    color: #ffffff;
    box-shadow:
        0 0 20px rgba(42, 74, 122, 0.4),
        0 4px 16px rgba(42, 74, 122, 0.3);
    transform: translateY(-2px);
}

.mission-tabs :deep(.ant-tabs-tab-active .ant-tabs-tab-btn) {
    color: #ffffff !important;
    text-shadow: 0 0 0.25px currentcolor;
}

.mission-tabs :deep(.ant-tabs-tab-active:hover) {
    background: linear-gradient(45deg, #1a4268, #3b6ea5);
    box-shadow:
        0 0 25px rgba(42, 74, 122, 0.5),
        0 6px 20px rgba(42, 74, 122, 0.4);
}

.mission-tabs :deep(.ant-tabs-tab-active:hover .ant-tabs-tab-btn) {
    color: #ffffff !important;
}

.mission-tabs :deep(.ant-tabs-content-holder) {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.98),
        rgba(248, 249, 250, 0.95)
    );
    border-radius: 12px;
    padding: 20px;
    border: 1px solid rgba(42, 74, 122, 0.25);
    box-shadow: 0 4px 16px rgba(42, 74, 122, 0.1);
    position: relative;
    overflow: hidden;
}

.mission-tabs :deep(.ant-tabs-content-holder::before) {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #64748b, #2a4a7a, #3b6ea5);
    opacity: 0.6;
}

.tab-mission-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

/* 文字呈現樣式 */
.tab-mission-header-text {
    padding: 16px 20px;
    margin-bottom: 20px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    border-radius: 12px;
    border-left: 4px solid rgba(42, 74, 122, 0.6);
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.1);
}

.node-title-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
}

.node-name-text {
    font-size: 16px;
    font-weight: 600;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.node-stats-text {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
    font-size: 13px;
    color: #4a5568;
    font-family: 'Courier New', monospace;
}

.stat-text-item {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #4a5568;
}

.stat-text-item.error {
    color: #ff4d4f;
}

.stat-text-item.cancelled {
    color: #ff7875;
}

.stat-icon {
    font-size: 14px;
    color: #2a4a7a;
}

.view-users-btn-text {
    padding: 0;
    height: auto;
    font-size: 13px;
    color: #2a4a7a !important;
    font-weight: 500;
    font-family: 'Courier New', monospace;
    transition: all 0.3s ease;
}

.view-users-btn-text:hover {
    color: #3b6ea5 !important;
    text-shadow: 0 0 8px rgba(138, 175, 255, 0.5);
}

.tab-accordion-header-text {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    padding-left: 0;
    /* 章節和任務的 header 不需要額外縮進，由父容器控制 */
}

.tab-step-item-text {
    padding: 12px 16px;
    margin-bottom: 8px;
    background: rgba(255, 255, 255, 0.6);
    border-left: 3px solid rgba(208, 255, 183, 0.5);
    border-radius: 6px;
    transition: all 0.2s ease;
}

.tab-step-item-text:hover {
    background: rgba(255, 255, 255, 0.8);
    border-left-color: rgba(208, 255, 183, 0.8);
}

.tab-chapters-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding-left: 24px;
    /* 章節縮進 */
    border-left: 2px solid rgba(138, 175, 255, 0.2);
    /* 左側邊框表示層級 */
    margin-left: 8px;
}

.tab-chapter-accordion :deep(.ant-collapse-item) {
    border: 1px solid rgba(138, 175, 255, 0.25);
    border-left: 4px solid rgba(138, 175, 255, 0.6);
    border-radius: 8px;
    margin-bottom: 12px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.9),
        rgba(248, 249, 250, 0.8)
    );
    box-shadow: 0 2px 8px rgba(138, 175, 255, 0.1);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
}

.tab-chapter-accordion :deep(.ant-collapse-item::before) {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(
        180deg,
        rgba(138, 175, 255, 0.8),
        rgba(168, 255, 246, 0.6)
    );
    transition: width 0.3s ease;
}

.tab-chapter-accordion :deep(.ant-collapse-item:hover) {
    box-shadow: 0 4px 12px rgba(138, 175, 255, 0.2);
    transform: translateX(2px);
}

.tab-chapter-accordion :deep(.ant-collapse-item:hover::before) {
    width: 6px;
}

.tab-chapter-accordion :deep(.ant-collapse-header) {
    padding: 10px 14px !important;
    background: transparent !important;
}

.tab-chapter-accordion :deep(.ant-collapse-content) {
    background: rgba(255, 255, 255, 0.5) !important;
    border: none !important;
}

.tab-chapter-accordion :deep(.ant-collapse-content-box) {
    padding: 10px 14px !important;
}

.tab-accordion-header {
    width: 100%;
}

.tab-accordion-header :deep(.node-card) {
    margin-bottom: 0;
    padding: 8px;
    border-radius: 6px;
}

.tab-tasks-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-left: 24px;
    /* 任務縮進 */
    border-left: 2px solid rgba(168, 255, 246, 0.2);
    /* 左側邊框表示層級 */
    margin-left: 8px;
    margin-top: 8px;
}

.tab-task-accordion :deep(.ant-collapse-item) {
    border: 1px solid rgba(168, 255, 246, 0.25);
    border-left: 4px solid rgba(168, 255, 246, 0.6);
    border-radius: 8px;
    margin-bottom: 10px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.85),
        rgba(248, 249, 250, 0.75)
    );
    box-shadow: 0 2px 8px rgba(168, 255, 246, 0.1);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
}

.tab-task-accordion :deep(.ant-collapse-item::before) {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(
        180deg,
        rgba(168, 255, 246, 0.8),
        rgba(208, 255, 183, 0.6)
    );
    transition: width 0.3s ease;
}

.tab-task-accordion :deep(.ant-collapse-item:hover) {
    box-shadow: 0 4px 12px rgba(168, 255, 246, 0.2);
    transform: translateX(2px);
}

.tab-task-accordion :deep(.ant-collapse-item:hover::before) {
    width: 6px;
}

.tab-task-accordion :deep(.ant-collapse-header) {
    padding: 8px 12px !important;
    background: transparent !important;
}

.tab-task-accordion :deep(.ant-collapse-content) {
    background: rgba(255, 255, 255, 0.4) !important;
    border: none !important;
}

.tab-task-accordion :deep(.ant-collapse-content-box) {
    padding: 8px 12px !important;
}

.tab-steps-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding-left: 24px;
    /* 步驟縮進 */
    border-left: 2px solid rgba(208, 255, 183, 0.2);
    /* 左側邊框表示層級 */
    margin-left: 8px;
    margin-top: 8px;
}

.tab-step-item {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.9),
        rgba(248, 249, 250, 0.8)
    );
    border: 1px solid rgba(208, 255, 183, 0.3);
    border-left: 4px solid rgba(208, 255, 183, 0.7);
    border-radius: 8px;
    padding: 12px;
    margin-bottom: 8px;
    box-shadow: 0 2px 6px rgba(208, 255, 183, 0.15);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;
}

.tab-step-item::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 4px;
    height: 100%;
    background: linear-gradient(
        180deg,
        rgba(208, 255, 183, 0.8),
        rgba(255, 158, 207, 0.4)
    );
    transition: width 0.3s ease;
}

.tab-step-item:hover {
    box-shadow: 0 4px 12px rgba(208, 255, 183, 0.25);
    transform: translateX(2px);
    border-color: rgba(208, 255, 183, 0.5);
}

.tab-step-item:hover::before {
    width: 6px;
}

.tab-step-item :deep(.node-card) {
    margin-bottom: 0;
    padding: 8px;
    border-radius: 4px;
}

.chapter-tree-collapse :deep(.ant-collapse-header),
.task-tree-collapse :deep(.ant-collapse-header) {
    background: transparent !important;
    padding: 10px 12px !important;
    border: none !important;
}

.chapter-tree-collapse :deep(.ant-collapse-content),
.task-tree-collapse :deep(.ant-collapse-content) {
    background: rgba(255, 255, 255, 0.3);
    border: none;
    border-top: 1px solid rgba(42, 74, 122, 0.1);
    padding: 8px 12px 12px 12px;
    border-radius: 0 0 8px 8px;
}

.chapter-tree-collapse :deep(.ant-collapse-content-box) {
    padding: 0;
}

.chapters-container,
.tasks-container,
.steps-container {
    margin-left: 0;
    padding-left: 0;
}

.steps-container {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 10px;
    margin-top: 8px;
}

.step-item {
    background: rgba(255, 255, 255, 0.7);
    border: 1px solid rgba(42, 74, 122, 0.2);
    border-left: 3px solid rgba(168, 255, 246, 0.5);
    border-radius: 8px;
    padding: 12px;
    transition: all 0.3s ease;
    box-shadow: 0 1px 3px rgba(42, 74, 122, 0.06);
}

.step-item:hover {
    background: rgba(255, 255, 255, 0.9);
    border-color: rgba(42, 74, 122, 0.3);
    border-left-color: rgba(168, 255, 246, 0.7);
    box-shadow: 0 2px 6px rgba(42, 74, 122, 0.1);
    transform: translateX(2px);
}

/* 響應式設計 */
@media (max-width: 1200px) {
    .missions-grid {
        grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
        gap: 16px;
    }
}

@media (max-width: 768px) {
    .missions-grid {
        grid-template-columns: 1fr;
        gap: 12px;
    }
}

/* 表格樣式 */
.progress-table :deep(.ant-table-tbody > tr) {
    transition: background-color 0.2s ease;
}

.progress-table :deep(.ant-table-tbody > tr:hover) {
    background-color: rgba(248, 249, 250, 0.8) !important;
}

.progress-table :deep(.ant-table-tbody > tr:hover > td) {
    background-color: rgba(248, 249, 250, 0.8) !important;
}

/* 確保 hover 時 tag 顏色不變 */
.progress-table :deep(.ant-table-tbody > tr:hover .ant-tag),
.progress-table :deep(.ant-table-tbody > tr:hover .rank-tag),
.progress-table :deep(.ant-table-tbody > tr:hover .department-tag),
.progress-table :deep(.ant-table-tbody > tr:hover .node-type-tag) {
    background-color: inherit !important;
    border-color: inherit !important;
    color: inherit !important;
}

/* 可點擊的 filter 元素 */
.clickable-filter {
    cursor: pointer;
    transition: color 0.2s ease;
    pointer-events: auto;
    user-select: none;
}

.clickable-filter:hover {
    color: #2a4a7a;
    text-decoration: underline;
}

/* 確保 tag 內的點擊事件能觸發 */
.rank-tag.clickable-filter,
.department-tag.clickable-filter {
    pointer-events: auto;
}

/* 階層 Popover 樣式 */
.hierarchy-popover {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 200px;
}

.hierarchy-popover-item {
    display: flex;
    gap: 8px;
    align-items: flex-start;
}

.hierarchy-popover-label {
    color: #718096;
    font-weight: 500;
    min-width: 50px;
    font-size: 13px;
}

.hierarchy-popover-value {
    color: #4a5568;
    font-size: 13px;
    flex: 1;
}

.email-text {
    color: #718096;
    font-size: 13px;
}

/* 低調但可見的 Tag 樣式 - 使用蒸氣波色系但降低飽和度 */
.rank-tag {
    background: rgba(42, 74, 122, 0.12) !important;
    border: 1px solid rgba(42, 74, 122, 0.25) !important;
    color: #8b5fbf !important;
    font-weight: 500;
}

.department-tag {
    background: rgba(138, 175, 255, 0.12) !important;
    border: 1px solid rgba(138, 175, 255, 0.25) !important;
    color: #6b8fcf !important;
    font-weight: 500;
}

.identity-tag {
    background: rgba(160, 180, 170, 0.12) !important;
    border: 1px solid rgba(160, 180, 170, 0.25) !important;
    color: #7a9085 !important;
    font-weight: 500;
}

.node-hierarchy-cell {
    display: flex;
    flex-direction: column;
}

.node-hierarchy-content {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.node-info {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}

.node-name {
    font-weight: 500;
    color: #4a5568;
    font-size: 14px;
}

/* 莫蘭迪色系 Tag 樣式 - 提高可見度但保持柔和 */
.node-type-tag {
    font-weight: 500;
}

.node-type-tag.node-type-mission {
    background: rgba(168, 147, 180, 0.25) !important;
    border: 1px solid rgba(168, 147, 180, 0.5) !important;
    color: #7a6a8a !important;
}

.node-type-tag.node-type-chapter {
    background: rgba(150, 175, 195, 0.25) !important;
    border: 1px solid rgba(150, 175, 195, 0.5) !important;
    color: #6a7f95 !important;
}

.node-type-tag.node-type-task {
    background: rgba(140, 165, 185, 0.25) !important;
    border: 1px solid rgba(140, 165, 185, 0.5) !important;
    color: #5a6f85 !important;
}

.node-type-tag.node-type-step {
    background: rgba(160, 180, 170, 0.25) !important;
    border: 1px solid rgba(160, 180, 170, 0.5) !important;
    color: #6a8075 !important;
}

.hierarchy-path {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    font-size: 13px;
    line-height: 1.6;
    color: #718096;
}

.hierarchy-segment {
    color: #718096;
    font-weight: 400;
}

.hierarchy-separator {
    color: #cbd5e0;
    margin: 0 2px;
}

.view-users-btn {
    padding: 0;
    height: auto;
    font-size: 12px;
    color: #2a4a7a !important;
    font-weight: 500;
    font-family: 'Courier New', monospace;
    transition: all 0.3s ease;
}

.view-users-btn:hover {
    color: #3b6ea5 !important;
    text-shadow: 0 0 8px rgba(138, 175, 255, 0.5);
    transform: translateX(2px);
}

.conditions-summary {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.summary-text {
    color: #4a5568;
    font-size: 13px;
}

.pending-review {
    color: #ffa87a;
    font-size: 12px;
    font-weight: 500;
}

.pending-review.clickable {
    cursor: pointer;
    text-decoration: underline;
    transition: all 0.2s ease;
}

.pending-review.clickable:hover {
    color: #64748b;
    text-shadow: 0 0 8px rgba(255, 158, 207, 0.5);
}

.progress-cell {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.progress-time {
    font-size: 12px;
    color: #718096;
    line-height: 1.5;
}

/* 節點參與者 Modal */
.node-users-content {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

/* 節點資訊摘要 */
.node-info-summary {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    border: 1px solid rgba(42, 74, 122, 0.2);
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.1);
}

.summary-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
    padding-bottom: 12px;
    border-bottom: 1px solid rgba(42, 74, 122, 0.15);
}

.summary-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.summary-type-tag {
    font-weight: 600;
}

.summary-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
}

.summary-stat-card {
    text-align: center;
    padding: 12px;
    background: rgba(255, 255, 255, 0.6);
    border-radius: 8px;
    border: 1px solid rgba(42, 74, 122, 0.15);
    transition: all 0.3s ease;
}

.summary-stat-card:hover {
    background: rgba(255, 255, 255, 0.9);
    border-color: rgba(42, 74, 122, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.15);
}

.summary-stat-card.highlight {
    background: linear-gradient(
        135deg,
        rgba(42, 74, 122, 0.1),
        rgba(138, 175, 255, 0.1)
    );
    border-color: rgba(42, 74, 122, 0.3);
}

.summary-stat-label {
    font-size: 12px;
    color: #6a6a7a;
    margin-bottom: 6px;
    font-family: 'Courier New', monospace;
}

.summary-stat-value {
    font-size: 20px;
    font-weight: 700;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.summary-stat-value.success {
    color: #52c41a;
}

.summary-stat-value.highlight {
    color: #2a4a7a;
    font-size: 24px;
}

/* 搜尋和篩選區域 */
.node-users-filters {
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
    flex-wrap: wrap;
    align-items: center;
}

.search-input {
    flex: 1;
    min-width: 200px;
}

.filter-select {
    min-width: 120px;
}

.search-btn {
    flex-shrink: 0;
}

.users-list-section {
    margin-top: 0;
}

/* 響應式設計 */
@media (max-width: 768px) {
    .missions-records-page {
        padding: 16px;
    }

    .content {
        padding: 20px;
    }

    .filter-row {
        flex-direction: column;
    }

    .filter-item {
        width: 100%;
    }

    .toolbar-actions {
        flex-direction: column;
        align-items: stretch;
    }

    .view-mode-switch {
        width: 100%;
    }
}

/* 編輯進度 Modal 快捷操作區 */
.quick-actions-bar {
    margin-bottom: 20px;
    padding: 16px;
    background: linear-gradient(
        135deg,
        rgba(255, 158, 207, 0.1),
        rgba(42, 74, 122, 0.1)
    );
    border: 1px solid rgba(42, 74, 122, 0.3);
    border-radius: 8px;
    display: flex;
    gap: 12px;
    align-items: center;
}

.quick-actions-bar::before {
    content: '快捷操作';
    font-size: 12px;
    color: #2a4a7a;
    font-weight: 600;
    margin-right: 8px;
    padding-right: 12px;
    border-right: 1px solid rgba(42, 74, 122, 0.3);
}

/* 編輯進度 Modal Footer 樣式 */
.edit-progress-footer {
    display: flex;
    justify-content: flex-end;
}

/* 確保編輯 Modal 顯示在查看參與者 Modal 之上 */
:deep(.ant-modal-wrap.edit-progress-modal-wrap) {
    z-index: 2000 !important;
}

/* 確保編輯 Modal 中的選擇框可以正常使用 */
.edit-progress-modal-wrap :deep(.ant-select) {
    pointer-events: auto !important;
    z-index: auto !important;
}

.edit-progress-modal-wrap :deep(.ant-select-selector) {
    pointer-events: auto !important;
    cursor: pointer !important;
}

.edit-progress-modal-wrap :deep(.ant-select-dropdown) {
    z-index: 2001 !important;
}

/* 操作按鈕樣式 */
.action-buttons {
    display: flex;
    gap: 4px;
}
</style>
